import { getStageHint, STATUS_LABELS } from './status-pipelines.js';
import { esc, todayISO, fmtAmt, fmtDate, fmtDateTime, ticketQuotedTotal, ticketPricingFromExtra } from './utils.js';
import { buildStatusPatchBody } from './assistant-stages.js';
import { tourQuotedTotal, tourPaxFromLead, buildQuotationStageData } from './assistant-tour-stages.js';
import { BOOKING_SYSTEMS, calcTicketQuoteTotals, isTicketQuickIntake, ticketIntakeText, isUnassignedPoolTicket } from './assistant-ticket-v2.js';

function ticketIntakeSummaryHtml(lead, { compact = false } = {}) {
  const extra = lead.extra || {};
  const intake = ticketIntakeText(lead);
  const meta = [
    esc(lead.name || 'Customer'),
    esc(lead.phone || '—'),
  ];
  if (extra.email) meta.push(esc(extra.email));
  if (lead.source) meta.push(esc(lead.source));
  const cls = compact ? 'stage-intake-card is-compact' : 'stage-intake-card';
  let html = `<div class="${cls}"><div class="stage-intake-meta">${meta.join(' · ')}</div>`;
  if (intake) {
    html += `<div class="stage-intake-details">${esc(intake).replace(/\n/g, '<br>')}</div>`;
  }
  html += '</div>';
  return html;
}

/** Highlighted customer request for unassigned pool detail view. */
export function renderTicketPoolIntakeMainHtml(lead) {
  const intake = ticketIntakeText(lead);
  const body = intake
    ? esc(intake).replace(/\n/g, '<br>')
    : '<span class="detail-intake-empty">No request details recorded.</span>';
  return `<div class="detail-intake-focus">
    <div class="detail-intake-request">
      <div class="detail-intake-request-label">Customer request</div>
      <div class="detail-intake-request-body">${body}</div>
    </div>
  </div>`;
}

function ticketPricingTotalsHtml(lead) {
  const pricing = ticketPricingFromExtra(lead.extra || {});
  const total = ticketQuotedTotal(lead);
  if (!total && !pricing) return '';
  return `<div class="stage-pricing-card">
    <div class="stage-pricing-grid">
      <div class="stage-pricing-cell"><span>Total cost</span><strong>${fmtAmt(pricing?.cost)}</strong></div>
      <div class="stage-pricing-cell"><span>Total profit</span><strong class="quote-profit">${fmtAmt(pricing?.profit)}</strong></div>
      <div class="stage-pricing-cell stage-pricing-total"><span>Total price</span><strong>${fmtAmt(total)}</strong></div>
    </div>
  </div>`;
}

function ticketQuoteSummaryHtml(lead) {
  const pricing = ticketPricingTotalsHtml(lead);
  if (isTicketQuickIntake(lead)) {
    const intake = ticketIntakeSummaryHtml(lead, { compact: true });
    if (!pricing) return intake;
    return intake + pricing;
  }
  if (!pricing) {
    return '<p class="stage-empty-hint">No quotation yet — use <strong>Edit</strong> to add est. cost and profit per passenger.</p>';
  }
  return pricing;
}

function ticketRequirementsSummaryHtml(lead) {
  if (isTicketQuickIntake(lead)) return ticketIntakeSummaryHtml(lead);
  const extra = lead.extra || {};
  const lines = [];
  lines.push(`<strong>${esc(lead.name || '—')}</strong> · ${esc(lead.phone || '—')}`);
  if (extra.email || lead.extra?.email) lines.push(`Email: ${esc(extra.email || '')}`);
  const route = lead.trip_type === 'multi' && extra.flight_segments?.length
    ? extra.flight_segments.map((s) => `${s.origin || '?'}→${s.destination || '?'}`).join(' · ')
    : `${lead.origin || '?'} → ${lead.destination || '?'}`;
  lines.push(`Route: ${esc(route)}`);
  const pax = extra.pax || {};
  const paxParts = [];
  if (pax.adt) paxParts.push(`${pax.adt} ADT`);
  if (pax.stu) paxParts.push(`${pax.stu} STU`);
  if (pax.chd) paxParts.push(`${pax.chd} CHD`);
  if (pax.inf) paxParts.push(`${pax.inf} INF`);
  if (paxParts.length) lines.push(`Passengers: ${paxParts.join(', ')}`);
  if (lead.airline) lines.push(`Airline: ${esc(lead.airline)}`);
  if (lead.ticket_class) lines.push(`Class: ${esc(lead.ticket_class)}`);
  const prefs = extra.preferences || {};
  const prefBits = [];
  if (prefs.baggage) prefBits.push(prefs.baggage);
  if (prefs.seat) prefBits.push(`${prefs.seat} seat`);
  if (prefs.transit === 'direct') prefBits.push('direct');
  else if (prefs.transit === 'multi' && prefs.multi_city_route) prefBits.push(`multi-city ${prefs.multi_city_route}`);
  else if (prefs.transit === 'transit' && prefs.transit_via) prefBits.push(`via ${prefs.transit_via}`);
  else if (prefs.transit === 'transit' || prefs.transit === 'ok') prefBits.push('transit OK');
  else if (prefs.transit === 'preferred' && prefs.transit_via) prefBits.push(`via ${prefs.transit_via}`);
  if (prefBits.length) lines.push(`Prefs: ${esc(prefBits.join(' · '))}`);
  return `<div class="stage-req-summary">${lines.map((l) => `<div>${l}</div>`).join('')}</div>`;
}

function quotationPaxFromLead(lead = {}) {
  const paxRaw = lead.extra?.pax || {};
  if (isTicketQuickIntake(lead) && !paxRaw.adt && !paxRaw.chd && !paxRaw.inf) {
    return { adt: 1, chd: 0, inf: 0 };
  }
  return paxRaw;
}

function quotationPaxFromForm(lead = {}) {
  if (stageField('stPaxAdt')) return paxFrom('stPax');
  return quotationPaxFromLead(lead);
}

function renderTicketQuotationQuickForm(lead, extra, sd) {
  const note = sd.quote_description || extra.quotation_note || sd.note || '';
  return ticketIntakeSummaryHtml(lead)
    + `<div class="stage-section stage-section-compact">
      <div class="stage-section-title">Send quotation</div>
      ${fg('stQuotedAt', 'Sent on', 'datetime-local', { value: toDatetimeLocal(extra.quoted_at || sd.quoted_at), required: true })}
      ${fg('stNote', 'Quotation details / notes', 'textarea', {
        rows: 6,
        value: note,
        placeholder: 'What you sent the client — route, airline, fare, conditions, etc.',
      })}
    </div>`;
}

function bookingSourceSelectValue(extra, sd) {
  const raw = String(extra.booking_system || sd.booking_system || '').trim();
  if (BOOKING_SYSTEMS.includes(raw)) return raw;
  if (raw) return 'Other';
  return '';
}

function bookingSourceOtherValue(extra, sd) {
  const raw = String(extra.booking_system || sd.booking_system || '').trim();
  if (raw && !BOOKING_SYSTEMS.includes(raw)) return raw;
  return extra.booking_system_other || sd.booking_system_other || '';
}

function renderBookingSourceFields(extra, sd) {
  const sel = bookingSourceSelectValue(extra, sd);
  const other = bookingSourceOtherValue(extra, sd);
  const opts = BOOKING_SYSTEMS.map((s) =>
    `<option value="${esc(s)}"${sel === s ? ' selected' : ''}>${esc(s)}</option>`
  ).join('');
  return `<div class="form-group">
      <label class="form-label" for="stBookingSystem">Where was the booking made? *</label>
      <select id="stBookingSystem" class="form-select" required>
        <option value="">Select…</option>${opts}
      </select>
    </div>
    <div class="form-group${sel === 'Other' ? '' : ' hidden'}" id="stBookingSystemOtherWrap">
      ${fg('stBookingSystemOther', 'Where (other)', 'text', { value: other, placeholder: 'Enter booking platform' })}
    </div>`;
}

function toggleBookingSystemOther(root = stageFormRoot()) {
  const sel = root.querySelector('#stBookingSystem')?.value;
  root.querySelector('#stBookingSystemOtherWrap')?.classList.toggle('hidden', sel !== 'Other');
}

function resolveBookingSystemFromForm() {
  const sel = txt('stBookingSystem');
  if (sel === 'Other') return txt('stBookingSystemOther') || '';
  return sel;
}

function renderTicketTempBookingQuickForm(lead, extra, sd) {
  const sdBook = extra.stage_data?.temporary_booking || sd;
  const pax = quotationPaxFromLead(lead);
  const costs = extra.pax_cost_rates || sdBook;
  const profits = extra.pax_profit_rates || sdBook;
  const expiryVal = toDatetimeLocal(extra.booking_expires || sdBook.booking_expires);
  return ticketIntakeSummaryHtml(lead)
    + `<div class="stage-section">
      <div class="stage-section-title">Quotation & pricing</div>
      ${paxCompact('stPax', pax)}
      <div class="form-row">${fg('stCostAdt', 'Cost / adult (LKR)', 'number', { value: costs.adt ?? sdBook.cost_adt ?? '', min: 0, required: true })}${fg('stProfitAdt', 'Profit / adult (LKR)', 'number', { value: profits.adt ?? sdBook.profit_adt ?? '', min: 0, required: true })}</div>
      <div class="form-row">${fg('stCostChd', 'Cost / child (LKR)', 'number', { value: costs.chd ?? sdBook.cost_chd ?? '', min: 0 })}${fg('stProfitChd', 'Profit / child (LKR)', 'number', { value: profits.chd ?? sdBook.profit_chd ?? '', min: 0 })}</div>
      <div class="form-row">${fg('stCostInf', 'Cost / infant (LKR)', 'number', { value: costs.inf ?? sdBook.cost_inf ?? '', min: 0 })}${fg('stProfitInf', 'Profit / infant (LKR)', 'number', { value: profits.inf ?? sdBook.profit_inf ?? '', min: 0 })}</div>
      <div id="stTicketQuoteTotals" class="stage-pricing-card"></div>
    </div>
    <div class="stage-section stage-section-compact">
      <div class="stage-section-title">Temporary booking</div>
      ${fg('stPnr', 'PNR / TKT no', 'text', { value: lead.pnr || sdBook.pnr, required: true })}
      ${renderBookingSourceFields(extra, sdBook)}
      ${fg('stExpiry', 'Booking expires', 'datetime-local', { value: expiryVal, required: true })}
      ${fg('stAdvance', 'Advance received (LKR)', 'number', { value: sdBook.advance ?? extra.advance_paid ?? '', min: 0 })}
      <input type="hidden" id="stQuotedTotal" value="">
    </div>`;
}

function stageFormHint(type, status, lead = {}) {
  if (type === 'ticket' && isTicketQuickIntake(lead)) {
    if (status === 'quotation_sent') return getStageHint(type, 'quotation_sent_v2');
    if (status === 'temporary_booking') return getStageHint(type, 'temporary_booking_v2');
  }
  return getStageHint(type, status);
}

function fg(id, label, type = 'text', opts = {}) {
  const req = opts.required ? ' *' : '';
  const val = opts.value != null && opts.value !== '' ? ` value="${esc(String(opts.value))}"` : '';
  const ph = opts.placeholder ? ` placeholder="${esc(opts.placeholder)}"` : '';
  const min = opts.min != null ? ` min="${opts.min}"` : '';
  const ro = opts.readonly ? ' readonly tabindex="-1"' : '';
  const cls = opts.className ? ` ${opts.className}` : '';
  if (type === 'textarea') {
    return `<div class="form-group"><label class="form-label">${esc(label)}${req}</label><textarea class="form-textarea${cls}" id="${id}" rows="${opts.rows || 2}">${esc(opts.value || '')}</textarea></div>`;
  }
  if (type === 'checkbox') {
    const checked = opts.checked ? ' checked' : '';
    return `<label class="check-row"><input type="checkbox" id="${id}"${checked}><span>${esc(label)}</span></label>`;
  }
  if (type === 'select') {
    const optsHtml = (opts.options || []).map((o) =>
      `<option value="${esc(o.value)}"${String(o.value) === String(opts.value || '') ? ' selected' : ''}>${esc(o.label)}</option>`
    ).join('');
    return `<div class="form-group"><label class="form-label">${esc(label)}${req}</label><select class="form-select${cls}" id="${id}"><option value="">Select…</option>${optsHtml}</select></div>`;
  }
  return `<div class="form-group"><label class="form-label">${esc(label)}${req}</label><input type="${type}" class="form-input${cls}" id="${id}"${val}${ph}${min}${ro}></div>`;
}

function toDatetimeLocal(val) {
  if (val == null || val === '') return '';
  const s = String(val).trim();
  if (s.includes('T')) return s.slice(0, 16);
  return `${s}T17:00`;
}

function datetimeLocalToDate(val) {
  if (!val) return null;
  return String(val).trim().split('T')[0] || null;
}

function paxRow(prefix, pax = {}) {
  return `<div class="form-row form-row-3">
    ${fg(`${prefix}Adt`, 'Adults (ADT)', 'number', { value: pax.adt, min: 0 })}
    ${fg(`${prefix}Chd`, 'Children (CHD)', 'number', { value: pax.chd, min: 0 })}
    ${fg(`${prefix}Inf`, 'Infants (INF)', 'number', { value: pax.inf, min: 0 })}
  </div>`;
}

function paxCompact(prefix, pax = {}, opts = {}) {
  const ro = opts.readonly ? ' readonly tabindex="-1"' : '';
  const cell = (suffix, short, val) =>
    `<label class="pax-cell"><span class="pax-cell-label">${short}</span><input type="number" class="form-input${opts.readonly ? ' stage-rate-readonly' : ''}" id="${prefix}${suffix}" value="${val ?? ''}" min="0" inputmode="numeric"${ro}></label>`;
  return `<div class="form-group pax-compact">
    <span class="form-label">Passengers</span>
    <div class="pax-compact-row">
      ${cell('Adt', 'ADT', pax.adt)}
      ${cell('Chd', 'CHD', pax.chd)}
      ${cell('Inf', 'INF', pax.inf)}
    </div>
  </div>`;
}

function checkChips(items) {
  return `<div class="check-chips">${items.map(({ id, label, checked }) =>
    `<label class="check-chip"><input type="checkbox" id="${id}"${checked ? ' checked' : ''}><span>${esc(label)}</span></label>`
  ).join('')}</div>`;
}

const ROOM_TYPES = [
  { v: 'single', l: 'Single' },
  { v: 'double_twin', l: 'Double/Twin' },
  { v: 'triple', l: 'Triple' },
  { v: 'family', l: 'Family' },
];

const CABIN_CLASSES = [
  { v: '', l: 'Any class' },
  { v: 'economy', l: 'Economy' },
  { v: 'premium_economy', l: 'Premium economy' },
  { v: 'business', l: 'Business' },
  { v: 'first', l: 'First' },
];

function fgSelect(id, label, options, value = '', opts = {}) {
  const req = opts.required ? ' *' : '';
  const optsHtml = options.map((o) =>
    `<option value="${esc(o.v)}"${String(o.v) === String(value || '') ? ' selected' : ''}>${esc(o.l)}</option>`
  ).join('');
  return `<div class="form-group"><label class="form-label">${esc(label)}${req}</label><select class="form-select" id="${id}">${optsHtml}</select></div>`;
}

function calcTourDurationFromRange(from, to) {
  if (!from || !to) return '';
  const a = new Date(`${from}T12:00:00`);
  const b = new Date(`${to}T12:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) return '';
  const nights = Math.round((b - a) / 86400000);
  const days = nights + 1;
  if (nights <= 0) return `${days}D`;
  return `${nights}N ${days}D`;
}

function formatDateRangeLabel(from, to) {
  if (!from || !to) return '';
  return `${fmtDate(from)} – ${fmtDate(to)}`;
}

function defaultFlightSeg() {
  return {
    departure: { airport: '', time: '' },
    transit: { airport: '', time: '', duration: '' },
    arrival: { airport: '', time: '' },
  };
}

function flightSegFields(prefix, seg = {}) {
  const dep = seg.departure || {};
  const tr = seg.transit || {};
  const arr = seg.arrival || {};
  return `
    <div class="form-row form-row-3">
      ${fg(`${prefix}DepAp`, 'Departure airport', 'text', { value: dep.airport, placeholder: 'CMB', className: 'route-code' })}
      ${fg(`${prefix}DepTime`, 'Departure time', 'datetime-local', { value: toDatetimeLocal(dep.time) })}
    </div>
    <div class="stage-sub-title-sm">Transit (if any)</div>
    <div class="form-row form-row-3">
      ${fg(`${prefix}TrAp`, 'Transit airport', 'text', { value: tr.airport, placeholder: 'DXB', className: 'route-code' })}
      ${fg(`${prefix}TrTime`, 'Transit timings', 'text', { value: tr.time, placeholder: 'Arr / dep' })}
      ${fg(`${prefix}TrDur`, 'Transit duration', 'text', { value: tr.duration, placeholder: '2h 30m' })}
    </div>
    <div class="form-row form-row-3">
      ${fg(`${prefix}ArrAp`, 'Arrival airport', 'text', { value: arr.airport, placeholder: 'LHR', className: 'route-code' })}
      ${fg(`${prefix}ArrTime`, 'Arrival time', 'datetime-local', { value: toDatetimeLocal(arr.time) })}
    </div>`;
}

function flightSegBlock(prefix, title, seg = {}, opts = {}) {
  const hidden = opts.hidden ? ' hidden' : '';
  return `<div class="stage-flight-seg${hidden}" id="${prefix}Block">
    <div class="stage-sub-title">${esc(title)}</div>
    ${flightSegFields(prefix, seg)}
  </div>`;
}

function hotelRoomRowHtml(i, room = {}) {
  const type = room.room_type || 'double_twin';
  const opts = ROOM_TYPES.map((o) =>
    `<option value="${esc(o.v)}"${o.v === type ? ' selected' : ''}>${esc(o.l)}</option>`
  ).join('');
  return `<div class="hotel-room-row" data-room-idx="${i}">
    <div class="hotel-room-head">
      <span class="stage-sub-title-sm">Room configuration ${i + 1}</span>
      ${i > 0 ? `<button type="button" class="btn btn-ghost btn-sm" data-room-remove="${i}">Remove</button>` : ''}
    </div>
    <div class="form-row form-row-4 hotel-room-grid">
      <div class="form-group"><label class="form-label">Room type</label><select class="form-select" data-field="room_type">${opts}</select></div>
      <div class="form-group"><label class="form-label">No. of rooms</label><input type="number" class="form-input" data-field="room_count" value="${room.room_count ?? 1}" min="1"></div>
      <div class="form-group"><label class="form-label">Adults / room</label><input type="number" class="form-input" data-field="adt" value="${room.adt ?? 2}" min="0"></div>
      <div class="form-group"><label class="form-label">Children / room</label><input type="number" class="form-input" data-field="chd" value="${room.chd ?? 0}" min="0"></div>
    </div>
    <div class="form-row"><div class="form-group"><label class="form-label">Infants / room</label><input type="number" class="form-input" data-field="inf" value="${room.inf ?? 0}" min="0"></div></div>
  </div>`;
}

function renderHotelRooms(rooms) {
  const list = rooms?.length ? rooms : [{ room_type: 'double_twin', room_count: 1, adt: 2, chd: 0, inf: 0 }];
  return `<div id="stHotelRooms">${list.map((r, i) => hotelRoomRowHtml(i, r)).join('')}</div>
    <button type="button" class="btn btn-ghost btn-sm" id="stAddHotelRoom">+ Add room type</button>`;
}

function collectFlightSeg(prefix) {
  const depAp = txt(`${prefix}DepAp`);
  const arrAp = txt(`${prefix}ArrAp`);
  const trAp = txt(`${prefix}TrAp`);
  const seg = {
    departure: { airport: depAp.toUpperCase(), time: txt(`${prefix}DepTime`) },
    transit: { airport: trAp.toUpperCase(), time: txt(`${prefix}TrTime`), duration: txt(`${prefix}TrDur`) },
    arrival: { airport: arrAp.toUpperCase(), time: txt(`${prefix}ArrTime`) },
  };
  const empty = !depAp && !arrAp && !trAp && !seg.departure.time && !seg.arrival.time;
  return empty ? null : seg;
}

function collectHotelRooms() {
  return [...document.querySelectorAll('#stHotelRooms .hotel-room-row')].map((row) => ({
    room_type: row.querySelector('[data-field="room_type"]')?.value || 'double_twin',
    room_count: Number(row.querySelector('[data-field="room_count"]')?.value) || 1,
    adt: Number(row.querySelector('[data-field="adt"]')?.value) || 0,
    chd: Number(row.querySelector('[data-field="chd"]')?.value) || 0,
    inf: Number(row.querySelector('[data-field="inf"]')?.value) || 0,
  }));
}

function renderHotelRoomsBox(rooms) {
  const box = document.getElementById('stHotelRooms');
  if (!box) return;
  const list = rooms?.length ? rooms : [{ room_type: 'double_twin', room_count: 1, adt: 2, chd: 0, inf: 0 }];
  box.innerHTML = list.map((r, i) => hotelRoomRowHtml(i, r)).join('');
}

function updateQuoteDurationFields() {
  const from = txt('stDateFrom');
  const to = txt('stDateTo');
  const dur = calcTourDurationFromRange(from, to);
  const durEl = document.getElementById('stDuration');
  const rangeEl = document.getElementById('stCustomDates');
  if (durEl) durEl.value = dur;
  if (rangeEl) rangeEl.value = formatDateRangeLabel(from, to);
}

function syncBaggageAllowance() {
  if (!cb('stBaggageSame')) return;
  const dep = document.getElementById('stBagDep');
  const arr = document.getElementById('stBagArr');
  if (dep && arr) arr.value = dep.value;
}

function toggleDomesticFlightBlock(show) {
  document.getElementById('stDomesticFlights')?.classList.toggle('hidden', !show);
}

function toggleReturnFlightBlock(show) {
  document.getElementById('stReturnFlight')?.classList.toggle('hidden', !show);
}

function quoteRequestClientForm(tqr = {}) {
  const flights = tqr.flights || {};
  const hotel = tqr.hotel || {};
  const rooms = hotel.rooms || (tqr.hotel?.room_type ? [{
    room_type: tqr.hotel.room_type,
    room_count: tqr.hotel.rooms || 1,
    adt: tqr.pax?.adt || 2,
    chd: tqr.pax?.chd || 0,
    inf: tqr.pax?.inf || 0,
  }] : null);
  const dateFrom = tqr.date_from || '';
  const dateTo = tqr.date_to || '';
  const duration = tqr.duration || calcTourDurationFromRange(dateFrom, dateTo) || '';
  const includeDomestic = flights.include_domestic ?? !!tqr.domestic;
  const includeReturn = flights.include_return ?? false;
  const baggage = flights.baggage || {};
  const sameBag = baggage.same !== false;

  return `<div class="stage-form-compact stage-form-quote-req"><div class="stage-grid">
    <div class="stage-block span-2">
      <div class="stage-block-title">Dates & passengers</div>
      <div class="form-row form-row-3">
        ${fg('stDateFrom', 'Requested from', 'date', { value: dateFrom, required: true })}
        ${fg('stDateTo', 'Requested to', 'date', { value: dateTo, required: true })}
        ${fg('stDuration', 'Duration', 'text', { value: duration, placeholder: 'Auto e.g. 4N 5D', readonly: true })}
      </div>
      <input type="hidden" id="stCustomDates" value="${esc(tqr.custom_dates || formatDateRangeLabel(dateFrom, dateTo))}">
      ${fg('stRecommendedDates', 'Recommended dates (cheap airfares)', 'textarea', { value: tqr.recommended_dates, rows: 4, placeholder: 'e.g. ±3 days flexible, mid-week departures', className: 'stage-field-long' })}
      ${paxCompact('stPax', tqr.pax)}
      ${fg('stChildAges', 'Children ages (if any)', 'text', { value: tqr.child_ages, placeholder: 'e.g. 5, 8' })}
    </div>

    <div class="stage-block span-2">
      <div class="stage-block-title">Flight details</div>
      <div class="form-row">
        ${fg('stAirline', 'Requested airline', 'text', { value: flights.airline, placeholder: 'Any / preferred airline' })}
        ${fgSelect('stCabinClass', 'Cabin class', CABIN_CLASSES, flights.cabin_class || '')}
      </div>
      ${flightSegBlock('stIntOut', 'International — outbound', flights.outbound || defaultFlightSeg())}
      <label class="check-row"><input type="checkbox" id="stIncludeReturn"${includeReturn ? ' checked' : ''}><span>Include return flight preferences</span></label>
      ${flightSegBlock('stIntRet', 'International — return', flights.return || defaultFlightSeg(), { hidden: !includeReturn })}
      <label class="check-row"><input type="checkbox" id="stIncludeDomestic"${includeDomestic ? ' checked' : ''}><span>Domestic flights included</span></label>
      <div id="stDomesticFlights" class="${includeDomestic ? '' : 'hidden'}">
        ${flightSegBlock('stDomOut', 'Domestic — outbound', flights.domestic_outbound || defaultFlightSeg())}
        ${flightSegBlock('stDomRet', 'Domestic — return', flights.domestic_return || defaultFlightSeg())}
      </div>
      <div class="stage-sub-title-sm">Baggage allowance</div>
      <div class="form-row">
        ${fg('stBagDep', 'Departure allowance', 'text', { value: baggage.departure || baggage.departure_allowance, placeholder: 'e.g. 30kg + 7kg cabin' })}
        ${fg('stBagArr', 'Return allowance', 'text', { value: sameBag ? (baggage.departure || baggage.departure_allowance) : (baggage.arrival || baggage.arrival_allowance), placeholder: 'e.g. 30kg + 7kg cabin' })}
      </div>
      <label class="check-row"><input type="checkbox" id="stBaggageSame"${sameBag ? ' checked' : ''}><span>Same allowance for departure and return</span></label>
      ${fg('stFlightReq', 'Other flight notes', 'textarea', { value: flights.notes || tqr.flight_requests, rows: 4, placeholder: 'Stops, layover limits, seat preferences…', className: 'stage-field-long' })}
    </div>

    <div class="stage-block span-2">
      <div class="stage-block-title">Hotel</div>
      ${renderHotelRooms(rooms)}
      <div class="form-row">
        ${fgSelect('stHotelStar', 'Preferred star rating', [
          { v: '', l: 'No preference' }, { v: '3', l: '3 star' }, { v: '4', l: '4 star' }, { v: '5', l: '5 star' },
        ], hotel.star_rating || '')}
        ${fg('stHotelArea', 'Preferred area / location', 'text', { value: hotel.preferred_area, placeholder: 'City / district / near landmark' })}
      </div>
      ${fg('stHotelNotes', 'Hotel notes', 'textarea', { value: hotel.notes, rows: 4, placeholder: 'Sea view, connecting rooms, late check-out…', className: 'stage-field-long' })}
    </div>

    <div class="stage-block">
      <div class="stage-block-title">Meals & transfers</div>
      ${checkChips([
        { id: 'stMealB', label: 'Breakfast', checked: tqr.meals?.breakfast },
        { id: 'stMealL', label: 'Lunch', checked: tqr.meals?.lunch },
        { id: 'stMealD', label: 'Dinner', checked: tqr.meals?.dinner },
        { id: 'stPrivateXfer', label: 'Private xfer', checked: tqr.private_transfers },
        { id: 'stAirportXfer', label: 'Airport xfer', checked: tqr.airport_transfers },
        { id: 'stGuide', label: 'Guide', checked: tqr.guide },
      ])}
      ${fg('stBudget', 'Target budget (LKR)', 'number', { value: tqr.budget, min: 0, placeholder: 'Optional total budget' })}
    </div>

    <div class="stage-block">
      <div class="stage-block-title">Extras</div>
      ${checkChips([
        { id: 'stVisaHelp', label: 'Visa assistance', checked: tqr.visa_assistance },
        { id: 'stInsurance', label: 'Travel insurance', checked: tqr.travel_insurance },
        { id: 'stFlexDates', label: 'Dates flexible', checked: tqr.dates_flexible },
      ])}
      ${fg('stSpecialReq', 'Special requirements', 'textarea', { value: tqr.special_requirements, rows: 4, placeholder: 'Dietary, mobility, honeymoon, anniversary…', className: 'stage-field-long' })}
    </div>

    <div class="stage-block span-2">
      <div class="stage-block-title">Sightseeing</div>
      ${fg('stPlaces', 'Places to visit / activities', 'textarea', { value: tqr.places_to_visit, rows: 5, placeholder: 'Must-see sights, tours, free days…', className: 'stage-field-long' })}
    </div>
  </div></div>`;
}

function initQuoteRequestClientForm(wrap) {
  if (!wrap.dataset.quoteReqBound) {
    wrap.dataset.quoteReqBound = '1';

    wrap.addEventListener('input', (e) => {
      if (e.target.id === 'stDateFrom' || e.target.id === 'stDateTo') updateQuoteDurationFields();
      if (e.target.id === 'stBagDep' && cb('stBaggageSame')) syncBaggageAllowance();
    });

    wrap.addEventListener('change', (e) => {
      if (e.target.id === 'stIncludeDomestic') toggleDomesticFlightBlock(cb('stIncludeDomestic'));
      if (e.target.id === 'stIncludeReturn') toggleReturnFlightBlock(cb('stIncludeReturn'));
      if (e.target.id === 'stBaggageSame') {
        const arr = document.getElementById('stBagArr');
        if (arr) arr.readOnly = cb('stBaggageSame');
        syncBaggageAllowance();
      }
    });

    wrap.addEventListener('click', (e) => {
      if (e.target.closest('#stAddHotelRoom')) {
        e.preventDefault();
        const rooms = collectHotelRooms();
        rooms.push({ room_type: 'double_twin', room_count: 1, adt: 2, chd: 0, inf: 0 });
        renderHotelRoomsBox(rooms);
        return;
      }
      const rm = e.target.closest('[data-room-remove]');
      if (rm) {
        e.preventDefault();
        const idx = Number(rm.dataset.roomRemove);
        const rooms = collectHotelRooms();
        if (rooms.length <= 1) return;
        rooms.splice(idx, 1);
        renderHotelRoomsBox(rooms);
      }
    });

    wrap.addEventListener('blur', (e) => {
      if (e.target.matches('.route-code')) {
        e.target.value = e.target.value.trim().toUpperCase();
      }
    }, true);
  }

  const arrBag = document.getElementById('stBagArr');
  if (arrBag) arrBag.readOnly = cb('stBaggageSame');
  toggleDomesticFlightBlock(cb('stIncludeDomestic'));
  toggleReturnFlightBlock(cb('stIncludeReturn'));
  updateQuoteDurationFields();
}

function collectQuoteRequestClientData() {
  const from = txt('stDateFrom');
  const to = txt('stDateTo');
  const sameBag = cb('stBaggageSame');
  const bagDep = txt('stBagDep');
  const bagArr = sameBag ? bagDep : txt('stBagArr');
  return {
    date_from: from,
    date_to: to,
    custom_dates: formatDateRangeLabel(from, to) || txt('stCustomDates'),
    duration: txt('stDuration') || calcTourDurationFromRange(from, to),
    recommended_dates: txt('stRecommendedDates'),
    pax: paxFrom('stPax'),
    child_ages: txt('stChildAges'),
    flights: {
      airline: txt('stAirline'),
      cabin_class: txt('stCabinClass'),
      outbound: collectFlightSeg('stIntOut'),
      return: cb('stIncludeReturn') ? collectFlightSeg('stIntRet') : null,
      include_return: cb('stIncludeReturn'),
      include_domestic: cb('stIncludeDomestic'),
      domestic_outbound: cb('stIncludeDomestic') ? collectFlightSeg('stDomOut') : null,
      domestic_return: cb('stIncludeDomestic') ? collectFlightSeg('stDomRet') : null,
      baggage: { departure: bagDep, arrival: bagArr, same: sameBag },
      notes: txt('stFlightReq'),
    },
    hotel: {
      rooms: collectHotelRooms(),
      star_rating: txt('stHotelStar'),
      preferred_area: txt('stHotelArea'),
      notes: txt('stHotelNotes'),
    },
    meals: { breakfast: cb('stMealB'), lunch: cb('stMealL'), dinner: cb('stMealD') },
    places_to_visit: txt('stPlaces'),
    private_transfers: cb('stPrivateXfer'),
    airport_transfers: cb('stAirportXfer'),
    guide: cb('stGuide'),
    budget: num('stBudget'),
    visa_assistance: cb('stVisaHelp'),
    travel_insurance: cb('stInsurance'),
    dates_flexible: cb('stFlexDates'),
    special_requirements: txt('stSpecialReq'),
  };
}

function fmtFlightSeg(label, seg) {
  if (!seg) return '';
  const parts = [];
  if (seg.departure?.airport) parts.push(`Dep ${seg.departure.airport}${seg.departure.time ? ` ${fmtWhen(seg.departure.time)}` : ''}`);
  if (seg.transit?.airport) {
    parts.push(`Via ${seg.transit.airport}${seg.transit.duration ? ` (${seg.transit.duration})` : ''}`);
  }
  if (seg.arrival?.airport) parts.push(`Arr ${seg.arrival.airport}${seg.arrival.time ? ` ${fmtWhen(seg.arrival.time)}` : ''}`);
  return parts.length ? `${label}: ${parts.join(' · ')}` : '';
}

function fmtRoomType(type) {
  return ROOM_TYPES.find((r) => r.v === type)?.l || type;
}

function fmtHotelRooms(rooms) {
  if (!rooms?.length) return '';
  return rooms.map((r) =>
    `${r.room_count || 1}× ${fmtRoomType(r.room_type)} (${r.adt || 0} ADT${r.chd ? `, ${r.chd} CHD` : ''}${r.inf ? `, ${r.inf} INF` : ''} / room)`
  ).join('; ');
}

function rateCompact(prefix, label, rates = {}, opts = {}) {
  const ro = opts.readonly ? ' readonly tabindex="-1"' : '';
  const cell = (suffix, short, val) =>
    `<label class="pax-cell pax-rate-cell"><span class="pax-cell-label">${short}</span><input type="number" class="form-input stage-rate-input${opts.readonly ? ' stage-rate-readonly' : ''}" id="${prefix}${suffix}" value="${val ?? ''}" min="0" inputmode="numeric"${ro}></label>`;
  return `<div class="form-group pax-compact pax-rates-compact">
    <span class="form-label">${esc(label)}</span>
    <div class="pax-compact-row pax-rate-row">
      ${cell('Adt', 'ADT', rates.adt)}
      ${cell('Chd', 'CHD', rates.chd)}
      ${cell('Inf', 'INF', rates.inf)}
    </div>
  </div>`;
}

function paxCompactInline(prefix, pax = {}) {
  const cell = (suffix, short, val) =>
    `<label class="pax-cell"><span class="pax-cell-label">${short}</span><input type="number" class="form-input stage-pax-input" id="${prefix}${suffix}" value="${val ?? ''}" min="0" inputmode="numeric"></label>`;
  return `<div class="form-group pax-compact pax-inline">
    <span class="form-label">Passengers</span>
    <div class="pax-compact-row">
      ${cell('Adt', 'ADT', pax.adt)}
      ${cell('Chd', 'CHD', pax.chd)}
      ${cell('Inf', 'INF', pax.inf)}
    </div>
  </div>`;
}

function stageTripTypeSeg(current) {
  const opts = [
    { v: 'one_way', l: 'One way' },
    { v: 'return', l: 'Return' },
    { v: 'multi', l: 'Multi-city' },
  ];
  return `<div class="mini-seg stage-trip-seg" id="stTripTypeSeg" role="group" aria-label="Trip type">
    ${opts.map((o) => `<button type="button" class="mini-seg-btn${current === o.v ? ' active' : ''}" data-value="${o.v}" aria-pressed="${current === o.v}">${o.l}</button>`).join('')}
  </div>
  <input type="hidden" id="stTripType" value="${esc(current || 'one_way')}">`;
}

function defaultStageSegment() {
  return { origin: '', destination: '', depart_date: '', depart_time: '', arrive_time: '' };
}

function stageMultiCitySegHtml(i, s, total) {
  const canRemove = total > 2;
  return `<div class="multi-city-seg stage-multi-seg" data-seg-idx="${i}">
    <div class="multi-city-seg-head">
      <span class="multi-city-seg-label">Flight ${i + 1}</span>
      ${canRemove ? `<button type="button" class="btn btn-ghost btn-sm seg-remove" data-st-seg-remove="${i}" aria-label="Remove flight ${i + 1}">Remove</button>` : ''}
    </div>
    <div class="multi-city-seg-fields">
      <div class="multi-city-seg-route">
        <div class="form-group">
          <label class="form-label">From</label>
          <input class="form-input seg-origin route-code" value="${esc(s.origin || '')}" placeholder="CMB" maxlength="8" autocomplete="off">
        </div>
        <span class="route-arrow" aria-hidden="true">→</span>
        <div class="form-group">
          <label class="form-label">To</label>
          <input class="form-input seg-dest route-code" value="${esc(s.destination || '')}" placeholder="DXB" maxlength="8" autocomplete="off">
        </div>
        <div class="form-group seg-date-wrap">
          <label class="form-label">Date</label>
          <input type="date" class="form-input seg-date" value="${esc(s.depart_date || '')}">
        </div>
      </div>
    </div>
  </div>`;
}

function renderStageMultiCitySegments(segments) {
  const segs = segments?.length >= 2 ? segments : [defaultStageSegment(), defaultStageSegment()];
  return segs.map((s, i) => stageMultiCitySegHtml(i, s, segs.length)).join('');
}

function deriveMarkup(cost = {}, quote = {}) {
  const mk = (k) => {
    if (quote[k] != null && cost[k] != null) return quote[k] - cost[k];
    if (quote[k] != null) return quote[k];
    return null;
  };
  return { adt: mk('adt'), chd: mk('chd'), inf: mk('inf') };
}

function sumRate(a, b) {
  const x = a == null ? 0 : Number(a);
  const y = b == null ? 0 : Number(b);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  if (a == null && b == null) return null;
  return x + y;
}

function quotationRouteBlock(lead, sd) {
  const tripType = lead.trip_type || sd.trip_type || 'one_way';
  const segments = sd.flight_segments || lead.extra?.flight_segments || [];
  const isMulti = tripType === 'multi';
  const isReturn = tripType === 'return';
  const retVal = lead.return_date || sd.return_date || '';
  return `<div class="stage-block span-2">
    <div class="stage-block-title">Route & dates</div>
    ${stageTripTypeSeg(tripType)}
    <div id="stSimpleRoute" class="${isMulti ? 'hidden' : ''}">
      <div class="form-row">${fg('stOrigin', 'From', 'text', { value: lead.origin || sd.origin, placeholder: 'CMB' })}${fg('stDest', 'To', 'text', { value: lead.destination || sd.destination, placeholder: 'DXB' })}</div>
      <div class="form-row">${fg('stDepart', 'Depart', 'date', { value: lead.depart_date || sd.depart_date })}<div class="form-group${isReturn ? '' : ' hidden'}" id="stReturnWrap"><label class="form-label">Return</label><input type="date" class="form-input" id="stReturn" value="${esc(retVal)}"></div></div>
    </div>
    <div id="stMultiRoute" class="stage-multi-wrap${isMulti ? '' : ' hidden'}">
      <div id="stMultiCitySegments">${renderStageMultiCitySegments(segments)}</div>
      <button type="button" class="btn btn-ghost btn-sm" id="stAddMultiSeg">+ Add flight</button>
    </div>
  </div>`;
}

function calcQuotationTotal() {
  const pax = paxFrom('stPax');
  const cost = ratesFrom('stCost');
  const markup = ratesFrom('stMarkup');
  let total = 0;
  let hasValue = false;
  for (const [key, count] of [['adt', pax.adt], ['chd', pax.chd], ['inf', pax.inf]]) {
    if (!count) continue;
    const unit = sumRate(cost[key], markup[key]);
    if (unit != null && unit > 0) {
      total += unit * count;
      hasValue = true;
    }
  }
  const el = document.getElementById('stTotal');
  if (el) el.value = hasValue ? String(Math.round(total)) : '';
}

function calcAgentQuoteTotal() {
  const pax = paxFrom('stPax');
  const rates = ratesFrom('stAgent');
  let total = 0;
  let hasValue = false;
  for (const [key, count] of [['adt', pax.adt], ['chd', pax.chd], ['inf', pax.inf]]) {
    if (!count) continue;
    const rate = rates[key];
    if (rate != null && rate > 0) {
      total += rate * count;
      hasValue = true;
    }
  }
  const el = document.getElementById('stTotal');
  if (el) el.value = hasValue ? String(Math.round(total)) : '';
}

function addRates(...vals) {
  let sum = 0;
  let hasAny = false;
  for (const v of vals) {
    if (v != null && Number.isFinite(Number(v))) {
      sum += Number(v);
      hasAny = true;
    }
  }
  return hasAny ? sum : null;
}

function setRateFields(prefix, rates = {}) {
  for (const suffix of ['Adt', 'Chd', 'Inf']) {
    const el = document.getElementById(`${prefix}${suffix}`);
    const key = suffix.toLowerCase();
    if (!el) continue;
    el.value = rates[key] != null ? String(Math.round(rates[key])) : '';
  }
}

function getAgentQuoteFromLead(extra = {}, lead = {}) {
  const agentSd = extra.stage_data?.quote_received_agent || {};
  return {
    agent_quote: agentSd.agent_quote || extra.agent_quote || {},
    pax: agentSd.pax || extra.pax || extra.tour_quote_request?.pax || {},
    agent_total: agentSd.total ?? lead.cost_amount ?? null,
  };
}

function quotedTotalFromFinalQuote(lead = {}, extra = {}) {
  const qSd = extra.stage_data?.quotation || {};
  const finalSd = extra.stage_data?.final_quote_sent || {};
  return qSd.sell_total ?? qSd.total ?? finalSd.total ?? lead.amount ?? extra.quoted_total ?? null;
}

function calcTourAdvanceBalance() {
  const quoted = num('stQuotedTotal');
  const advance = num('stAdvAmt');
  const remainEl = document.getElementById('stRemainBal');
  const banner = document.getElementById('stAdvancePayBanner');
  const collectWrap = document.getElementById('stBalCollectWrap');
  const collectInput = document.getElementById('stBalCollect');
  if (quoted == null || advance == null) {
    if (remainEl) remainEl.value = '';
    banner?.classList.add('hidden');
    collectWrap?.classList.add('hidden');
    if (collectInput) collectInput.required = false;
    return;
  }
  const remaining = Math.max(0, Math.round(quoted - advance));
  if (remainEl) remainEl.value = String(remaining);
  if (remaining > 0) {
    collectWrap?.classList.remove('hidden');
    if (collectInput) collectInput.required = true;
  } else {
    collectWrap?.classList.add('hidden');
    if (collectInput) {
      collectInput.required = false;
      collectInput.value = '';
    }
  }
  if (banner) {
    if (remaining > 0) {
      banner.classList.remove('hidden');
      banner.className = 'pay-status-banner pay-status-due';
      banner.textContent = `Collect LKR ${remaining.toLocaleString('en-US')} by the date below`;
    } else {
      banner.classList.remove('hidden');
      banner.className = 'pay-status-banner pay-status-paid';
      banner.textContent = 'Fully paid with advance — no balance due';
    }
  }
}

function quotationFormToAnswers() {
  return {
    agent_rate_adt: num('stAgentAdt'),
    agent_rate_chd: num('stAgentChd'),
    agent_rate_inf: num('stAgentInf'),
    flight_cost_adt: num('stFlightAdt'),
    flight_cost_chd: num('stFlightChd'),
    flight_cost_inf: num('stFlightInf'),
    visa_cost_adt: num('stVisaAdt'),
    visa_cost_chd: num('stVisaChd'),
    visa_cost_inf: num('stVisaInf'),
    final_rate_adt: num('stFinalAdt'),
    final_rate_chd: num('stFinalChd'),
    final_rate_inf: num('stFinalInf'),
    add_hotel_rooms: cb('stAddHotel'),
    hotel_rooms_summary: cb('stAddHotel') ? (txt('stHotelSummary') || null) : null,
    supplier_note: txt('stSupplierNote') || null,
  };
}

function calcQuotationModalTotals(lead = {}) {
  const qd = buildQuotationStageData(lead, quotationFormToAnswers());
  const costEl = document.getElementById('stCostTotalDisp');
  const sellEl = document.getElementById('stSellTotalDisp');
  const profitEl = document.getElementById('stProfitDisp');
  const perCostEl = document.getElementById('stCostPerDisp');
  if (costEl) costEl.textContent = fmtAmt(qd.cost_total);
  if (sellEl) sellEl.textContent = fmtAmt(qd.sell_total);
  if (profitEl) profitEl.textContent = fmtAmt(qd.profit_total);
  if (perCostEl && qd.total_cost_per) {
    const parts = [`ADT ${fmtAmt(qd.total_cost_per.adt)}`];
    if (qd.pax?.chd > 0) parts.push(`CHD ${fmtAmt(qd.total_cost_per.chd ?? qd.total_cost_per.adt)}`);
    if (qd.pax?.inf > 0) parts.push(`INF ${fmtAmt(qd.total_cost_per.inf ?? 0)}`);
    perCostEl.textContent = parts.join(' · ');
  }
}

function quotationForm(lead, extra, sd = {}) {
  const pax = tourPaxFromLead(lead);
  const agent = sd.agent_quote || extra.agent_quote || {};
  const flight = sd.flight_cost || extra.flight_cost || {};
  const visa = sd.visa_cost || extra.visa_cost || {};
  const final = sd.final_quote || extra.final_quote || {};
  const hotelOn = !!(sd.hotel_rooms_summary || sd.hotel_notes || extra.hotel_notes);
  const paxHint = fmtPaxLine(pax);
  const profit = (sd.sell_total ?? lead.amount ?? 0) - (sd.cost_total ?? lead.cost_amount ?? 0);
  return `<div class="stage-form-compact stage-form-quotation"><div class="stage-grid">
    <div class="stage-block span-2">
      <div class="stage-block-title">Cost — supplier / DMC${paxHint ? `<span class="field-hint"> · ${esc(paxHint)}</span>` : ''}</div>
      ${rateCompact('stAgent', 'Agent net cost / person (LKR)', agent)}
      ${rateCompact('stFlight', 'Flight fare / person (LKR) — optional', flight)}
      ${rateCompact('stVisa', 'Visa fee / person (LKR) — optional', visa)}
      <div class="stage-pricing-card">
        <div class="field-hint" id="stCostPerDisp">Per-person total = tour + flight + visa</div>
        <div class="stage-pricing-grid">
          <div class="stage-pricing-cell stage-pricing-total"><span>Grand total cost</span><strong id="stCostTotalDisp">${fmtAmt(sd.cost_total ?? lead.cost_amount)}</strong></div>
        </div>
      </div>
    </div>
    <div class="stage-block span-2">
      <div class="stage-block-title">Client quote</div>
      ${rateCompact('stFinal', 'Final quote / person (LKR)', final)}
      <div class="stage-pricing-card">
        <div class="stage-pricing-grid">
          <div class="stage-pricing-cell stage-pricing-total"><span>Quote total</span><strong id="stSellTotalDisp">${fmtAmt(sd.sell_total ?? lead.amount)}</strong></div>
          <div class="stage-pricing-cell"><span>Profit</span><strong class="quote-profit" id="stProfitDisp">${Number.isFinite(profit) ? fmtAmt(profit) : '—'}</strong></div>
        </div>
      </div>
    </div>
    <div class="stage-block span-2">
      <div class="stage-block-title">Optional</div>
      ${fg('stAddHotel', 'Add hotel room details', 'checkbox', { checked: hotelOn })}
      <div id="stHotelWrap"${hotelOn ? '' : ' class="hidden"'}>
        ${fg('stHotelSummary', 'Room details', 'textarea', { rows: 2, value: sd.hotel_rooms_summary || sd.hotel_notes || extra.hotel_notes || '', placeholder: 'e.g. Double x1 (2 pax), Single x1 (1 pax)' })}
      </div>
      ${fg('stSupplierNote', 'Supplier / DMC notes', 'textarea', { rows: 2, value: sd.supplier_note || sd.note || '' })}
    </div>
  </div></div>`;
}

function tourAdvanceForm(lead, extra, sd = {}) {
  const quoted = tourQuotedTotal(lead) || sd.quoted_total || quotedTotalFromFinalQuote(lead, extra);
  const advance = sd.advance_amount ?? extra.advance_paid ?? '';
  const remaining = sd.balance_due ?? extra.balance_due
    ?? (quoted != null && advance !== '' && advance != null ? Math.max(0, Math.round(Number(quoted) - Number(advance))) : '');
  const balanceDue = remaining !== '' && remaining != null ? Number(remaining) : null;
  const needsCollectDate = balanceDue == null || balanceDue > 0;
  const quoteHint = quoted ? `<div class="field-hint">Quote total: ${fmtAmt(quoted)}</div>` : '';
  return `<div class="stage-form-tour-advance">
    <input type="hidden" id="stQuotedTotal" value="${quoted ?? ''}">
    ${fg('stTravelDate', 'Travel date', 'date', { value: extra.travel_date || sd.travel_date || lead.depart_date, required: true })}
    ${quoteHint}
    ${fg('stAdvAmt', 'Advance received (LKR)', 'number', { value: advance, required: true, min: 0, className: 'stage-rate-input' })}
    ${fg('stAdvDate', 'Advance paid on', 'date', { value: extra.advance_date || sd.advance_date || todayISO(), required: true })}
    ${fg('stRemainBal', 'Balance due (LKR)', 'number', { value: remaining, readonly: true })}
    <div id="stBalCollectWrap"${needsCollectDate ? '' : ' class="hidden"'}>
      ${fg('stBalCollect', 'Balance collect by', 'date', { value: extra.balance_collect_date || sd.balance_collect_date, required: needsCollectDate })}
    </div>
    <div id="stAdvancePayBanner" class="pay-status-banner hidden" aria-live="polite"></div>
  </div>`;
}

function calcFinalQuoteTotals() {
  const agent = ratesFrom('stRefAgent');
  const pax = paxFrom('stRefPax');
  const flight = ratesFrom('stFlight');
  const visa = ratesFrom('stVisa');
  const profit = ratesFrom('stProfit');
  const extraFees = ratesFrom('stExtra');
  const final = {
    adt: addRates(agent.adt, flight.adt, visa.adt, profit.adt, extraFees.adt),
    chd: addRates(agent.chd, flight.chd, visa.chd, profit.chd, extraFees.chd),
    inf: addRates(agent.inf, flight.inf, visa.inf, profit.inf, extraFees.inf),
  };
  setRateFields('stFinal', final);
  let total = 0;
  let hasValue = false;
  for (const [key, count] of [['adt', pax.adt], ['chd', pax.chd], ['inf', pax.inf]]) {
    if (!count) continue;
    const rate = final[key];
    if (rate != null && rate > 0) {
      total += rate * count;
      hasValue = true;
    }
  }
  const el = document.getElementById('stTotal');
  if (el) el.value = hasValue ? String(Math.round(total)) : '';
}

function finalQuoteSentForm(lead, extra, sd = {}) {
  const ref = getAgentQuoteFromLead(extra, lead);
  const agentQuote = sd.agent_quote || ref.agent_quote;
  const pax = sd.pax || ref.pax;
  const components = sd.quote_components || {
    flight_fare: sd.flight_fare,
    visa_fee: sd.visa_fee,
    profit_markup: sd.profit_markup,
    additional_fees: sd.additional_fees,
  };
  const agentHint = ref.agent_total != null
    ? `<div class="field-hint">Agent total cost: ${fmtAmt(ref.agent_total)}</div>`
    : '';
  return `<div class="stage-form-compact stage-form-final-quote"><div class="stage-grid">
    <div class="stage-block span-2">
      <div class="stage-block-title">From quote received from agent</div>
      ${rateCompact('stRefAgent', 'Agent quote / person (LKR)', agentQuote, { readonly: true })}
      ${paxCompact('stRefPax', pax, { readonly: true })}
      ${agentHint}
    </div>
    <div class="stage-block span-2">
      <div class="stage-block-title">Add to per-person price</div>
      ${rateCompact('stFlight', 'Flight fare (LKR)', components.flight_fare || {})}
      ${rateCompact('stVisa', 'Visa fee (LKR)', components.visa_fee || {})}
      ${rateCompact('stProfit', 'Profit markup (LKR)', components.profit_markup || {})}
      ${rateCompact('stExtra', 'Additional fees (LKR)', components.additional_fees || {})}
      ${fg('stExtraDesc', 'Additional fees description', 'text', { value: sd.additional_fees_desc, placeholder: 'e.g. Single supplement, peak season surcharge' })}
    </div>
    <div class="stage-block span-2">
      <div class="stage-block-title">Final quote to client</div>
      ${rateCompact('stFinal', 'Final quote / person (LKR)', sd.final_quote || extra.final_quote || {}, { readonly: true })}
      ${fg('stTotal', 'Total quotation (LKR)', 'number', { value: lead.amount || sd.total, min: 0, readonly: true })}
      ${fg('stNote', 'Notes (optional)', 'textarea', { rows: 2, value: sd.note || '' })}
    </div>
  </div></div>`;
}

function collectStageMultiCitySegments() {
  const rows = [...document.querySelectorAll('#stMultiCitySegments .stage-multi-seg')];
  return rows.map((row) => ({
    origin: row.querySelector('.seg-origin')?.value.trim().toUpperCase() || '',
    destination: row.querySelector('.seg-dest')?.value.trim().toUpperCase() || '',
    depart_date: row.querySelector('.seg-date')?.value || '',
    depart_time: '',
    arrive_time: '',
  }));
}

function renderStageMultiCityBox(segments) {
  const box = document.getElementById('stMultiCitySegments');
  if (!box) return;
  const segs = segments?.length >= 2 ? segments : [defaultStageSegment(), defaultStageSegment()];
  box.innerHTML = segs.map((s, i) => stageMultiCitySegHtml(i, s, segs.length)).join('');
}

function setStageTripType(tripType) {
  const hidden = document.getElementById('stTripType');
  if (hidden) hidden.value = tripType;
  document.querySelectorAll('#stTripTypeSeg .mini-seg-btn').forEach((btn) => {
    const on = btn.dataset.value === tripType;
    btn.classList.toggle('active', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  document.getElementById('stSimpleRoute')?.classList.toggle('hidden', tripType === 'multi');
  document.getElementById('stMultiRoute')?.classList.toggle('hidden', tripType !== 'multi');
  document.getElementById('stReturnWrap')?.classList.toggle('hidden', tripType !== 'return');
  if (tripType === 'multi') {
    const box = document.getElementById('stMultiCitySegments');
    if (box && !box.querySelector('.stage-multi-seg')) {
      const origin = document.getElementById('stOrigin')?.value.trim().toUpperCase() || '';
      const dest = document.getElementById('stDest')?.value.trim().toUpperCase() || '';
      const date = document.getElementById('stDepart')?.value || '';
      const seed = origin || dest || date
        ? [{ origin, destination: dest, depart_date: date, depart_time: '', arrive_time: '' }, defaultStageSegment()]
        : [defaultStageSegment(), defaultStageSegment()];
      renderStageMultiCityBox(seed);
    }
  }
}

function quotedTotalFromLead(lead = {}) {
  if (lead.type === 'ticket') {
    const q = ticketQuotedTotal(lead);
    return q != null && q !== '' ? q : '';
  }
  const extra = lead.extra || {};
  const q = lead.quote_amount ?? extra.stage_data?.quotation_sent?.total ?? lead.fare_amount ?? lead.amount;
  return q != null && q !== '' ? q : '';
}

function calcTempBookingBalance() {
  const quoted = num('stQuotedTotal');
  const advance = num('stAdvance') || 0;
  const balEl = document.getElementById('stBalance');
  if (!balEl) return;
  if (quoted == null) {
    balEl.value = '';
    return;
  }
  balEl.value = String(Math.max(0, Math.round(quoted - advance)));
}

function calcTicketQuotationModalTotals(lead = {}) {
  const box = stageFormRoot().querySelector('#stTicketQuoteTotals') || document.getElementById('stTicketQuoteTotals');
  if (!box) return;
  const pax = quotationPaxFromForm(lead);
  const rates = {
    cost_adt: num('stCostAdt'),
    profit_adt: num('stProfitAdt'),
    cost_chd: num('stCostChd'),
    profit_chd: num('stProfitChd'),
    cost_inf: num('stCostInf'),
    profit_inf: num('stProfitInf'),
  };
  const breakdown = calcTicketQuoteTotals(pax, rates);
  const parts = [];
  if (breakdown.cost) parts.push(`Net cost ${fmtAmt(breakdown.cost)}`);
  if (breakdown.profit) parts.push(`Profit ${fmtAmt(breakdown.profit)}`);
  const paxHint = [pax.adt ? `${pax.adt} ADT` : '', pax.chd ? `${pax.chd} CHD` : '', pax.inf ? `${pax.inf} INF` : ''].filter(Boolean).join(' · ');
  const hidden = stageFormRoot().querySelector('#stQuotedTotal');
  if (hidden) hidden.value = breakdown.total ? String(breakdown.total) : '';
  box.innerHTML = breakdown.total
    ? `<strong>Quote total: ${fmtAmt(breakdown.total)}</strong>${paxHint ? `<span class="field-hint">${esc(paxHint)}</span>` : ''}${parts.length ? `<span class="field-hint">${parts.join(' · ')}</span>` : ''}`
    : '<span class="field-hint">Enter passengers, cost, and profit to see the quote total.</span>';
  if (document.getElementById('stAdvance') && document.getElementById('stQuotedTotal')) {
    calcTempBookingBalance();
  }
}

function advancePaidFromLead(lead = {}) {
  const extra = lead.extra || {};
  return extra.advance_paid ?? extra.stage_data?.temporary_booking?.advance ?? 0;
}

function tempBalanceFromLead(lead = {}) {
  const extra = lead.extra || {};
  return extra.balance_due ?? extra.stage_data?.temporary_booking?.balance_due ?? '';
}

function calcBookingConfirmedStatus() {
  const quoted = num('stQuotedTotal') || 0;
  const advance = num('stAdvancePaid') || 0;
  const payment = num('stPayAmt') || 0;
  const totalPaid = advance + payment;
  const statusEl = document.getElementById('stPayStatus');
  const remainWrap = document.getElementById('stRemainWrap');
  const remainBal = document.getElementById('stRemainBal');
  if (!statusEl) return;

  if (quoted > 0 && totalPaid >= quoted) {
    statusEl.className = 'pay-status-banner pay-status-paid';
    statusEl.textContent = 'FULL PRICE PAID';
    statusEl.classList.remove('hidden');
    remainWrap?.classList.add('hidden');
  } else if (quoted > 0) {
    const remaining = Math.max(0, Math.round(quoted - totalPaid));
    statusEl.className = 'pay-status-banner pay-status-due';
    statusEl.textContent = `Balance still due: ${fmtAmt(remaining)}`;
    statusEl.classList.remove('hidden');
    remainWrap?.classList.remove('hidden');
    if (remainBal) remainBal.value = String(remaining);
  } else {
    statusEl.classList.add('hidden');
    remainWrap?.classList.add('hidden');
  }
}

export function initStageForm(type, status, lead = {}, wrapId = 'stStageFields') {
  const key = `${type}:${status}`;
  const wrap = document.getElementById(wrapId);
  if (!wrap) return;

  if (key === 'ticket:quotation_sent') {
    if (isTicketQuickIntake(lead)) return;
    if (!wrap.dataset.ticketQuoteBound) {
      wrap.dataset.ticketQuoteBound = '1';
      wrap.addEventListener('input', (e) => {
        if (/^st(Cost|Profit)(Adt|Chd|Inf)$/.test(e.target.id || '') || /^stPax(Adt|Chd|Inf)$/.test(e.target.id || '')) {
          calcTicketQuotationModalTotals(lead);
        }
      });
    }
    calcTicketQuotationModalTotals(lead);
    return;
  }

  if (key === 'tour:quotation') {
    if (!wrap.dataset.quotationBound) {
      wrap.dataset.quotationBound = '1';
      wrap.querySelector('#stAddHotel')?.addEventListener('change', (e) => {
        document.getElementById('stHotelWrap')?.classList.toggle('hidden', !e.target.checked);
      });
      wrap.addEventListener('input', (e) => {
        if (/^st(Agent|Flight|Visa|Final)/.test(e.target.id || '')) calcQuotationModalTotals(lead);
      });
    }
    calcQuotationModalTotals(lead);
    return;
  }

  if (key === 'tour:quote_request_client') {
    initQuoteRequestClientForm(wrap);
    return;
  }

  if (key === 'tour:quote_received_agent') {
    if (!wrap.dataset.agentQuoteBound) {
      wrap.dataset.agentQuoteBound = '1';
      wrap.addEventListener('input', (e) => {
        if (e.target.id?.startsWith('stAgent') || e.target.id?.startsWith('stPax')) {
          calcAgentQuoteTotal();
        }
      });
    }
    calcAgentQuoteTotal();
    return;
  }

  if (key === 'tour:final_quote_sent') {
    if (!wrap.dataset.finalQuoteBound) {
      wrap.dataset.finalQuoteBound = '1';
      wrap.addEventListener('input', (e) => {
        if (e.target.id?.startsWith('stFlight') || e.target.id?.startsWith('stVisa')
          || e.target.id?.startsWith('stProfit') || e.target.id?.startsWith('stExtra')) {
          calcFinalQuoteTotals();
        }
      });
    }
    calcFinalQuoteTotals();
    return;
  }

  if (key === 'tour:tour_accepted_advance') {
    if (!wrap.dataset.tourAdvanceBound) {
      wrap.dataset.tourAdvanceBound = '1';
      wrap.addEventListener('input', (e) => {
        if (e.target.id === 'stAdvAmt' || e.target.id === 'stQuotedTotal') calcTourAdvanceBalance();
      });
    }
    calcTourAdvanceBalance();
    return;
  }

  if (key === 'ticket:temporary_booking') {
    if (!wrap.dataset.tempBookingBound) {
      wrap.dataset.tempBookingBound = '1';
      wrap.addEventListener('input', (e) => {
        if (e.target.id === 'stAdvance') calcTempBookingBalance();
        if (isTicketQuickIntake(lead) && (
          /^st(Cost|Profit)(Adt|Chd|Inf)$/.test(e.target.id || '')
          || /^stPax(Adt|Chd|Inf)$/.test(e.target.id || '')
        )) {
          calcTicketQuotationModalTotals(lead);
        }
      });
      wrap.addEventListener('change', (e) => {
        if (e.target.id === 'stBookingSystem') toggleBookingSystemOther(wrap);
      });
    }
    toggleBookingSystemOther(wrap);
    if (isTicketQuickIntake(lead)) calcTicketQuotationModalTotals(lead);
    else calcTempBookingBalance();
    return;
  }

  if (key === 'ticket:booking_confirmed') {
    if (!wrap.dataset.bookingConfBound) {
      wrap.dataset.bookingConfBound = '1';
      wrap.addEventListener('input', (e) => {
        if (e.target.id === 'stPayAmt') calcBookingConfirmedStatus();
      });
    }
    calcBookingConfirmedStatus();
  }
}

function rateRow(prefix, label, rates = {}) {
  return rateCompact(prefix, label, rates);
}

export function renderStageForm(type, status, lead = {}) {
  const extra = lead.extra || {};
  const sd = extra.stage_data?.[status] || {};
  const tqr = extra.tour_quote_request || sd;

  if (status === 'client_not_interested') {
    const html = type === 'ticket'
      ? fg('stCloseReason', 'Reason', 'select', {
        value: sd.reason || '',
        options: [
          { value: 'declined', label: 'Client declined quote' },
          { value: 'paused', label: 'Paused — may contact later' },
          { value: 'other', label: 'Other' },
        ],
      }) + fg('stNote', 'Notes', 'textarea', { rows: 2, value: sd.note || '' })
      : fg('stNote', 'Reason', 'textarea', { required: true, rows: 3 });
    return { hint: stageFormHint(type, status, lead), html };
  }

  if (status === 'booking_cancelled') {
    return {
      hint: stageFormHint(type, status, lead),
      html: fg('stPenalty', 'Penalty charged (LKR)', 'number', { value: sd.penalty ?? extra.cancellation_penalty ?? '', min: 0 })
        + fg('stRefund', 'Amount refunded to client (LKR)', 'number', { value: sd.refund ?? extra.refund_amount ?? '', min: 0 })
        + fg('stNote', 'Notes', 'textarea', { rows: 3, value: sd.note || '', required: true }),
    };
  }

  if (status === 'new' && type === 'ticket' && isTicketQuickIntake(lead)) {
    return {
      hint: isUnassignedPoolTicket(lead)
        ? ''
        : 'Proceed to quotation when you are ready to send a quote.',
      html: renderTicketPoolIntakeMainHtml(lead),
    };
  }

  if (status === 'new' && type === 'ticket') {
    return {
      hint: 'Intake on file — claim from pool, send quote, then advance.',
      html: ticketRequirementsSummaryHtml(lead)
        + fg('stNote', 'Agent notes (optional)', 'textarea', { rows: 2, value: sd.note || '' }),
    };
  }

  if (status === 'new') {
    return {
      hint: 'Optionally note why this inquiry was reopened.',
      html: fg('stNote', 'Reason for reopening (optional)', 'textarea', { rows: 3 }),
    };
  }

  let html = '';
  switch (`${type}:${status}`) {
    case 'visa:details_provided':
      html = fg('stAmount', 'Quoted price (LKR)', 'number', { value: lead.amount, required: true, min: 0 })
        + fg('stNote', 'Notes (optional)', 'textarea', { rows: 2 });
      break;
    case 'visa:visa_applied':
      html = fg('stAppId', 'Application ID', 'text', { value: extra.application_id || sd.application_id, required: true })
        + fg('stPassport', 'Passport no', 'text', { value: extra.passport_no || sd.passport_no, required: true });
      break;
    case 'visa:visa_issued':
      html = fg('stIssueDate', 'Issue date', 'date', { value: extra.visa_issued_date || sd.issue_date })
        + fg('stNote', 'Notes (optional)', 'textarea', { rows: 2 });
      break;

    case 'ticket:quotation_sent': {
      if (isTicketQuickIntake(lead)) {
        html = renderTicketQuotationQuickForm(lead, extra, sd);
        break;
      }
      const pax = extra.pax || {};
      const costs = extra.pax_cost_rates || sd;
      const profits = extra.pax_profit_rates || sd;
      const systems = BOOKING_SYSTEMS;
      html = ticketRequirementsSummaryHtml(lead)
        + `<div class="stage-section">
          <div class="stage-section-title">Quotation sent</div>
          ${fg('stQuotedAt', 'Sent on', 'datetime-local', { value: toDatetimeLocal(extra.quoted_at || sd.quoted_at), required: true })}
          ${fg('stAirline', 'Airline', 'text', { value: lead.airline || sd.airline || '', required: true })}
          ${fg('stNote', 'Quote description / client message', 'textarea', { rows: 3, value: sd.quote_description || extra.quotation_note || sd.note || '' })}
          ${pax.adt > 0 ? `<div class="form-row">${fg('stCostAdt', `Cost / ADT (×${pax.adt})`, 'number', { value: costs.adt ?? sd.cost_adt ?? '', min: 0, required: true })}${fg('stProfitAdt', `Profit / ADT`, 'number', { value: profits.adt ?? sd.profit_adt ?? '', min: 0, required: true })}</div>` : ''}
          ${pax.chd > 0 ? `<div class="form-row">${fg('stCostChd', `Cost / CHD (×${pax.chd})`, 'number', { value: costs.chd ?? sd.cost_chd ?? '', min: 0 })}${fg('stProfitChd', `Profit / CHD`, 'number', { value: profits.chd ?? sd.profit_chd ?? '', min: 0 })}</div>` : ''}
          ${pax.inf > 0 ? `<div class="form-row">${fg('stCostInf', `Cost / INF (×${pax.inf})`, 'number', { value: costs.inf ?? sd.cost_inf ?? '', min: 0 })}${fg('stProfitInf', `Profit / INF`, 'number', { value: profits.inf ?? sd.profit_inf ?? '', min: 0 })}</div>` : ''}
          <div id="stTicketQuoteTotals" class="stage-pricing-card"></div>
          ${fg('stQuoteValid', 'Quote valid till', 'date', { value: sd.quote_valid_till || extra.quote_valid_till || '' })}
        </div>`;
      break;
    }
    case 'ticket:temporary_booking': {
      if (isTicketQuickIntake(lead)) {
        html = renderTicketTempBookingQuickForm(lead, extra, sd);
        break;
      }
      const sdBook = extra.stage_data?.temporary_booking || sd;
      const quoteTotal = quotedTotalFromLead(lead) || sdBook.quoted_total || '';
      const expiryVal = toDatetimeLocal(extra.booking_expires || sdBook.booking_expires);
      html = ticketPricingTotalsHtml(lead)
        + `<input type="hidden" id="stQuotedTotal" value="${quoteTotal || ''}">`
        + `<div class="stage-section stage-section-compact">
          <div class="stage-section-title">Temporary booking</div>
          ${fg('stPnr', 'PNR / TKT no', 'text', { value: lead.pnr || sdBook.pnr, required: true })}
          ${renderBookingSourceFields(extra, sdBook)}
          ${fg('stExpiry', 'Booking expires', 'datetime-local', { value: expiryVal, required: true })}
          ${fg('stAdvance', 'Advance received (LKR)', 'number', { value: sdBook.advance ?? extra.advance_paid ?? '', min: 0 })}
        </div>`;
      break;
    }
    case 'ticket:booking_confirmed': {
      const sdConf = extra.stage_data?.booking_confirmed || sd;
      const quoteTotal = quotedTotalFromLead(lead) || sdConf.quoted_total || '';
      const advance = advancePaidFromLead(lead);
      const tempBal = tempBalanceFromLead(lead);
      const defaultPay = tempBal !== '' && tempBal != null ? tempBal : Math.max(0, (quoteTotal || 0) - (advance || 0));
      html = ticketQuoteSummaryHtml(lead)
        + `<div class="stage-readonly-grid">
          <div><span>Advance paid</span><strong>${fmtAmt(advance)}</strong></div>
          <div><span>Balance due</span><strong>${fmtAmt(tempBal !== '' ? tempBal : defaultPay)}</strong></div>
        </div>`
        + `<input type="hidden" id="stQuotedTotal" value="${quoteTotal || ''}">`
        + `<input type="hidden" id="stAdvancePaid" value="${advance || 0}">`
        + `<div class="stage-section stage-form-payment">
          <div class="stage-section-title">Record payment</div>
          ${fg('stPaidOn', 'Payment received on', 'date', { value: extra.paid_on || sdConf.paid_on || todayISO(), required: true })}
          ${fg('stPayAmt', 'Amount received now (LKR)', 'number', { value: sdConf.payment_amount ?? defaultPay ?? '', required: true, min: 0, className: 'stage-rate-input' })}
          <div id="stPayStatus" class="pay-status-banner hidden" aria-live="polite"></div>
          <div id="stRemainWrap" class="hidden">
            ${fg('stRemainBal', 'Balance still due (LKR)', 'number', { value: sdConf.balance_due ?? extra.balance_due ?? '', readonly: true })}
            ${fg('stRemainPayDate', 'Remaining payment due by', 'datetime-local', { value: toDatetimeLocal(extra.remain_pay_by || sdConf.remain_pay_by), required: true })}
          </div>
          ${fg('stNote', 'Notes (optional)', 'textarea', { rows: 2, value: sdConf.note || '' })}
        </div>`;
      break;
    }
    case 'ticket:ticket_issued': {
      const sdIss = extra.stage_data?.ticket_issued || sd;
      const quoted = quotedTotalFromLead(lead) || 0;
      const advance = advancePaidFromLead(lead);
      const defaultPay = Math.max(0, quoted - advance);
      const intakeBlock = isTicketQuickIntake(lead) ? ticketIntakeSummaryHtml(lead, { compact: true }) : '';
      html = intakeBlock + ticketPricingTotalsHtml(lead)
        + `<div class="stage-section stage-section-compact">
          <div class="stage-section-title">Ticket issued</div>
          ${fg('stIssueDate', 'Issued date', 'date', { value: extra.ticket_issued_date || sdIss.issued_date || todayISO(), required: true })}
          ${fg('stPayAmt', 'Payment received (LKR)', 'number', { value: sdIss.payment_amount ?? defaultPay, min: 0 })}
          ${fg('stPnr', 'PNR / TKT no (confirm)', 'text', { value: lead.pnr || sdIss.pnr, required: true })}
          ${fg('stNote', 'Notes (e.g. PDF sent)', 'textarea', { rows: 2, value: sdIss.note || '' })}
        </div>`;
      break;
    }

    case 'tour:quotation':
      html = quotationForm(lead, extra, sd);
      break;
    case 'tour:tour_accepted_advance':
      html = tourAdvanceForm(lead, extra, sd);
      break;
    case 'tour:tour_accepted_progress':
      html = fg('stProgressNotes', 'Progress notes', 'textarea', {
        value: sd.progress_notes || extra.ticket_booking_details || sd.ticket_details || '',
        rows: 2,
        placeholder: 'e.g. EK 654 booked, visa applied 260910',
      })
        + fg('stFollowupDate', 'Next follow-up date', 'date', { value: lead.followup_date || sd.followup_date || '' })
        + fg('stTicketBooked', 'Tickets booked on', 'date', { value: extra.ticket_booked_on || sd.ticket_booked_on || '' })
        + `<div class="field-hint">Fill both ticket &amp; visa dates to auto-finalise on save.</div>`
        + fg('stVisaIssued', 'Visa issued date', 'date', { value: extra.visa_issued_date || sd.visa_issued_date || sd.visa_applied_date || '' });
      break;
    case 'tour:tour_finalised':
      html = fg('stNote', 'Closing notes', 'textarea', { rows: 3, value: sd.note || '' });
      break;

    case 'tour:details_provided':
      html = fg('stNote', 'Notes (optional)', 'textarea', { rows: 2 });
      break;
    case 'tour:quote_request_client':
      html = quoteRequestClientForm(tqr);
      break;
    case 'tour:quote_request_agent':
      html = fg('stNote', 'Request details / supplier note', 'textarea', { rows: 3 })
        + fg('stFollow', 'Follow-up date', 'date', { value: lead.followup_date || todayISO() });
      break;
    case 'tour:quote_received_agent': {
      const agentPax = sd.pax || extra.pax || extra.tour_quote_request?.pax;
      html = rateRow('stAgent', 'Agent quote per person (LKR)', extra.agent_quote || sd.agent_quote)
        + paxCompact('stPax', agentPax)
        + fg('stTotal', 'Total (LKR)', 'number', { value: lead.cost_amount || sd.total, min: 0, readonly: true });
      break;
    }
    case 'tour:final_quote_sent':
      html = finalQuoteSentForm(lead, extra, sd);
      break;
    default:
      html = fg('stNote', 'Notes (optional)', 'textarea', { rows: 2 });
      break;
  }

  return { hint: stageFormHint(type, status, lead), html: html ? `<div class="stage-workflow-form">${html}</div>` : '' };
}

function num(id) {
  const v = stageField(id)?.value;
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function txt(id) {
  return String(stageField(id)?.value || '').trim();
}

function cb(id) {
  return !!stageField(id)?.checked;
}

/** Prefer the open status modal form; fall back to the detail panel form. */
function stageFormRoot() {
  const modalOpen = document.getElementById('statusModal')?.classList.contains('open');
  const modalHost = document.getElementById('stStageFields');
  const panelHost = document.getElementById('dStageFields');
  if (modalOpen && modalHost?.querySelector('[id^="st"]')) return modalHost;
  if (panelHost?.querySelector('[id^="st"]')) return panelHost;
  if (modalHost?.querySelector('[id^="st"]')) return modalHost;
  return document;
}

function stageField(id) {
  const root = stageFormRoot();
  if (root !== document) {
    const scoped = root.querySelector(`#${CSS.escape(id)}`);
    if (scoped) return scoped;
  }
  return document.getElementById(id);
}

function paxFrom(prefix) {
  return { adt: num(`${prefix}Adt`) || 0, chd: num(`${prefix}Chd`) || 0, inf: num(`${prefix}Inf`) || 0 };
}

function ratesFrom(prefix) {
  return { adt: num(`${prefix}Adt`), chd: num(`${prefix}Chd`), inf: num(`${prefix}Inf`) };
}

export function collectStageFormData(type, status, lead = {}) {
  const body = { stage_data: {} };
  const d = body.stage_data;

  if (status === 'client_not_interested') {
    body.note = txt('stNote') || txt('stCloseReason');
    if (type === 'ticket') {
      d.reason = txt('stCloseReason');
      d.note = body.note;
    }
    return body;
  }

  if (status === 'booking_cancelled') {
    d.penalty = num('stPenalty');
    d.refund = num('stRefund');
    d.note = txt('stNote');
    body.note = d.note;
    return body;
  }

  if (status === 'new') {
    body.note = txt('stNote');
    return body;
  }

  switch (`${type}:${status}`) {
    case 'visa:details_provided':
      body.amount = num('stAmount');
      d.quote_price = body.amount;
      body.note = txt('stNote');
      break;
    case 'visa:visa_applied':
      d.application_id = txt('stAppId');
      d.passport_no = txt('stPassport');
      break;
    case 'visa:visa_issued':
      d.issue_date = txt('stIssueDate');
      body.note = txt('stNote');
      break;

    case 'ticket:quotation_sent': {
      if (isTicketQuickIntake(lead)) {
        d.quoted_at = txt('stQuotedAt');
        d.quote_description = txt('stNote') || null;
        body.note = d.quote_description;
        body.extra_patch = {
          quotation_note: d.quote_description,
          quoted_at: d.quoted_at,
        };
        break;
      }
      const pax = stageField('stPaxAdt') ? paxFrom('stPax') : (lead?.extra?.pax || {});
      const rates = {
        cost_adt: num('stCostAdt'),
        profit_adt: num('stProfitAdt'),
        cost_chd: num('stCostChd'),
        profit_chd: num('stProfitChd'),
        cost_inf: num('stCostInf'),
        profit_inf: num('stProfitInf'),
      };
      const breakdown = calcTicketQuoteTotals(pax, rates);
      d.quoted_at = txt('stQuotedAt');
      d.airline = txt('stAirline');
      d.quote_description = txt('stNote') || null;
      d.quote_valid_till = txt('stQuoteValid') || null;
      Object.assign(d, rates, breakdown);
      body.airline = d.airline;
      body.note = d.quote_description;
      body.quote_amount = breakdown.total;
      body.amount = breakdown.total;
      body.cost_amount = breakdown.cost;
      body.extra_patch = {
        pax,
        pax_cost_rates: { adt: rates.cost_adt, chd: rates.cost_chd, inf: rates.cost_inf },
        pax_profit_rates: { adt: rates.profit_adt, chd: rates.profit_chd, inf: rates.profit_inf },
        quoted_total: breakdown.total,
        quotation_note: d.quote_description,
        quote_valid_till: d.quote_valid_till,
        quoted_at: d.quoted_at,
      };
      break;
    }
    case 'ticket:temporary_booking': {
      const bookingSystem = resolveBookingSystemFromForm();
      if (isTicketQuickIntake(lead)) {
        const pax = quotationPaxFromForm(lead);
        const rates = {
          cost_adt: num('stCostAdt'),
          profit_adt: num('stProfitAdt'),
          cost_chd: num('stCostChd'),
          profit_chd: num('stProfitChd'),
          cost_inf: num('stCostInf'),
          profit_inf: num('stProfitInf'),
        };
        const breakdown = calcTicketQuoteTotals(pax, rates);
        d.pnr = txt('stPnr');
        body.pnr = d.pnr;
        d.booking_system = bookingSystem;
        d.booking_system_other = txt('stBookingSystem') === 'Other' ? txt('stBookingSystemOther') : null;
        d.booking_expires = txt('stExpiry');
        Object.assign(d, rates, breakdown);
        d.quoted_total = breakdown.total;
        d.advance = num('stAdvance') ?? 0;
        d.balance_due = d.quoted_total != null ? Math.max(0, Math.round(d.quoted_total - d.advance)) : null;
        body.quote_amount = breakdown.total;
        body.amount = breakdown.total;
        body.cost_amount = breakdown.cost;
        body.followup_date = datetimeLocalToDate(d.booking_expires) || null;
        body.extra_patch = {
          pax,
          pax_cost_rates: { adt: rates.cost_adt, chd: rates.cost_chd, inf: rates.cost_inf },
          pax_profit_rates: { adt: rates.profit_adt, chd: rates.profit_chd, inf: rates.profit_inf },
          quoted_total: breakdown.total,
          booking_system: bookingSystem,
          booking_system_other: d.booking_system_other,
          booking_expires: d.booking_expires,
          advance_paid: d.advance,
          balance_due: d.balance_due,
        };
        break;
      }
      d.pnr = txt('stPnr');
      body.pnr = d.pnr;
      d.booking_system = bookingSystem;
      d.booking_system_other = txt('stBookingSystem') === 'Other' ? txt('stBookingSystemOther') : null;
      d.booking_expires = txt('stExpiry');
      d.quoted_total = num('stQuotedTotal');
      d.advance = num('stAdvance') ?? 0;
      d.balance_due = d.quoted_total != null ? Math.max(0, Math.round(d.quoted_total - d.advance)) : null;
      body.followup_date = datetimeLocalToDate(d.booking_expires) || null;
      body.extra_patch = {
        booking_system: bookingSystem,
        booking_system_other: d.booking_system_other,
        booking_expires: d.booking_expires,
        advance_paid: d.advance,
        balance_due: d.balance_due,
      };
      break;
    }
    case 'ticket:booking_confirmed': {
      d.paid_on = txt('stPaidOn');
      d.payment_amount = num('stPayAmt');
      d.quoted_total = num('stQuotedTotal');
      d.advance_paid = num('stAdvancePaid');
      d.temp_balance = num('stTempBalance');
      const quoted = d.quoted_total || 0;
      const totalPaid = (d.advance_paid || 0) + (d.payment_amount || 0);
      d.balance_due = quoted > 0 ? Math.max(0, Math.round(quoted - totalPaid)) : null;
      d.fully_paid = quoted > 0 && totalPaid >= quoted;
      d.remain_pay_by = d.fully_paid ? null : txt('stRemainPayDate');
      body.sold_amount = d.payment_amount;
      body.note = txt('stNote');
      body.followup_date = d.fully_paid ? null : datetimeLocalToDate(d.remain_pay_by);
      break;
    }
    case 'ticket:ticket_issued':
      d.issued_date = txt('stIssueDate');
      d.pnr = txt('stPnr');
      body.pnr = d.pnr;
      d.payment_amount = num('stPayAmt');
      d.note = txt('stNote');
      body.note = d.note;
      body.sold_amount = d.payment_amount;
      break;

    case 'tour:quotation': {
      const patch = buildStatusPatchBody('tour', 'quotation', lead, quotationFormToAnswers());
      Object.assign(body, patch);
      break;
    }
    case 'tour:quote_request_client':
      Object.assign(d, collectQuoteRequestClientData());
      break;
    case 'tour:quote_request_agent':
      body.note = txt('stNote');
      body.followup_date = txt('stFollow') || null;
      break;
    case 'tour:quote_received_agent':
      d.agent_quote = ratesFrom('stAgent');
      d.pax = paxFrom('stPax');
      d.total = num('stTotal');
      body.cost_amount = d.total;
      break;
    case 'tour:final_quote_sent': {
      d.agent_quote = ratesFrom('stRefAgent');
      d.pax = paxFrom('stRefPax');
      d.flight_fare = ratesFrom('stFlight');
      d.visa_fee = ratesFrom('stVisa');
      d.profit_markup = ratesFrom('stProfit');
      d.additional_fees = ratesFrom('stExtra');
      d.additional_fees_desc = txt('stExtraDesc');
      d.quote_components = {
        flight_fare: d.flight_fare,
        visa_fee: d.visa_fee,
        profit_markup: d.profit_markup,
        additional_fees: d.additional_fees,
      };
      d.final_quote = ratesFrom('stFinal');
      d.total = num('stTotal');
      body.amount = d.total;
      body.note = txt('stNote');
      break;
    }
    case 'tour:tour_accepted_advance': {
      const patch = buildStatusPatchBody('tour', 'tour_accepted_advance', lead, {
        travel_date: txt('stTravelDate'),
        advance_amount: num('stAdvAmt'),
        advance_date: txt('stAdvDate'),
        balance_collect_date: txt('stBalCollect') || null,
      });
      Object.assign(body, patch);
      break;
    }
    case 'tour:tour_accepted_progress': {
      const progressAnswers = {
        progress_notes: txt('stProgressNotes') || null,
        followup_date: txt('stFollowupDate') || null,
        ticket_booked_on: txt('stTicketBooked') || null,
        visa_issued_date: txt('stVisaIssued') || null,
      };
      const patch = buildStatusPatchBody('tour', 'tour_accepted_progress', lead, progressAnswers);
      Object.assign(body, patch);
      break;
    }
    case 'tour:tour_finalised': {
      const patch = buildStatusPatchBody('tour', 'tour_finalised', lead, { note: txt('stNote') || null });
      Object.assign(body, patch);
      break;
    }
    default:
      body.note = txt('stNote');
      break;
  }

  return body;
}

function fmtWhen(val) {
  if (!val) return '';
  if (String(val).includes('T')) return fmtDateTime(val);
  return fmtDate(val);
}

function fmtPaxLine(pax) {
  if (!pax) return '';
  const parts = [];
  if (pax.adt != null) parts.push(`${pax.adt} ADT`);
  if (pax.chd != null) parts.push(`${pax.chd} CHD`);
  if (pax.inf != null) parts.push(`${pax.inf} INF`);
  return parts.length ? `Passengers: ${parts.join(', ')}` : '';
}

function fmtRateLine(label, rates) {
  if (!rates) return '';
  const parts = [];
  if (rates.adt != null) parts.push(`ADT ${fmtAmt(rates.adt)}`);
  if (rates.chd != null) parts.push(`CHD ${fmtAmt(rates.chd)}`);
  if (rates.inf != null) parts.push(`INF ${fmtAmt(rates.inf)}`);
  return parts.length ? `${label}: ${parts.join(' · ')}` : '';
}

function fmtRouteSegments(segments) {
  if (!segments?.length) return '';
  return segments
    .filter((s) => s.origin || s.destination || s.depart_date)
    .map((s) => {
      const route = `${s.origin || '?'} → ${s.destination || '?'}`;
      return s.depart_date ? `${route} (${fmtDate(s.depart_date)})` : route;
    })
    .join(' · ');
}

function enrichStageDataFromLead(type, status, data, lead) {
  const out = { ...data };
  const ex = lead?.extra || {};
  if (type === 'ticket') {
    switch (status) {
      case 'quotation_sent':
        if (!out.quote_valid_till) out.quote_valid_till = ex.quote_valid_till;
        if (!out.note) out.note = ex.quotation_note;
        if (out.total == null) out.total = lead.quote_amount ?? lead.amount;
        break;
      case 'temporary_booking':
        if (!out.pnr) out.pnr = lead.pnr;
        if (!out.airline) out.airline = lead.airline;
        if (!out.booking_system) out.booking_system = ex.booking_system;
        if (!out.passport_no) out.passport_no = ex.passport_no;
        if (!out.booking_expires) out.booking_expires = ex.booking_expires;
        if (!out.pay_before) out.pay_before = ex.pay_before;
        if (out.quoted_total == null) out.quoted_total = ex.quoted_total ?? lead.quote_amount;
        if (out.advance == null) out.advance = ex.advance_paid;
        if (out.balance_due == null) out.balance_due = ex.balance_due;
        break;
      case 'booking_confirmed':
        if (!out.paid_on) out.paid_on = ex.paid_on;
        if (out.payment_amount == null) out.payment_amount = ex.last_payment_amount ?? lead.sold_amount;
        if (out.advance_paid == null) out.advance_paid = ex.advance_paid;
        if (out.quoted_total == null) out.quoted_total = ex.quoted_total ?? lead.quote_amount;
        if (out.balance_due == null) out.balance_due = ex.balance_due;
        if (out.fully_paid == null) out.fully_paid = ex.payment_complete || ex.balance_due === 0;
        if (!out.remain_pay_by) out.remain_pay_by = ex.remain_pay_by;
        break;
      case 'ticket_issued':
        if (!out.issued_date) out.issued_date = ex.ticket_issued_date;
        if (!out.pnr) out.pnr = lead.pnr;
        if (!out.passport_no) out.passport_no = ex.passport_no;
        break;
      default:
        break;
    }
  }
  if (type === 'tour' && status === 'quote_request_client') {
    const tqr = ex.tour_quote_request || {};
    if (!out.date_from) out.date_from = lead.depart_date;
    if (!out.date_to) out.date_to = lead.return_date;
    if (!out.pax) out.pax = ex.pax;
    if (!out.duration) out.duration = ex.tour_duration;
    if (!out.flights && (out.departure || out.arrival)) {
      out.flights = {
        outbound: {
          departure: { airport: '', time: out.departure || '' },
          arrival: { airport: '', time: out.arrival || '' },
        },
        notes: out.flight_requests,
      };
    }
    if (!out.hotel?.rooms?.length && tqr.hotel) out.hotel = { ...out.hotel, ...tqr.hotel };
    if (out.flight_notes && !out.flights?.notes) {
      out.flights = { ...(out.flights || {}), notes: out.flight_notes };
    }
    if (out.hotel_notes && !out.hotel?.notes) {
      out.hotel = { ...(out.hotel || {}), notes: out.hotel_notes };
    }
    if (!Object.keys(out).length && Object.keys(tqr).length) Object.assign(out, tqr);
  }
  return out;
}

/** Human-readable detail block for inquiry history status entries. */
export function formatStageHistoryDescription(type, status, data = {}, lead = {}, recordedAt = null) {
  const d = enrichStageDataFromLead(type, status, data || {}, lead);
  const lines = [];
  const when = recordedAt ? fmtDateTime(recordedAt) : '';

  switch (`${type}:${status}`) {
    case 'visa:details_provided':
      if (d.quote_price != null || lead.amount != null) lines.push(`Quoted: ${fmtAmt(d.quote_price ?? lead.amount)}`);
      if (d.note) lines.push(d.note);
      break;
    case 'visa:visa_applied':
      if (d.application_id) lines.push(`Application ID: ${d.application_id}`);
      if (d.passport_no) lines.push(`Passport: ${d.passport_no}`);
      break;
    case 'visa:visa_issued':
      if (d.issue_date) lines.push(`Issued: ${fmtDate(d.issue_date)}`);
      break;

    case 'ticket:quotation_sent':
      if (d.quoted_at) lines.push(`Sent on: ${fmtWhen(d.quoted_at)}`);
      if (d.quote_description || d.note) lines.push(d.quote_description || d.note);
      if (d.quote_valid_till) lines.push(`Valid till: ${fmtDate(d.quote_valid_till)}`);
      if (d.total != null) lines.push(`Total quote: ${fmtAmt(d.total)}`);
      break;

    case 'ticket:temporary_booking':
      if (d.airline || lead.airline) lines.push(`Airline: ${d.airline || lead.airline}`);
      if (d.total != null) lines.push(`Total quoted: ${fmtAmt(d.total)}`);
      if (d.pnr) lines.push(`PNR: ${d.pnr}`);
      if (d.booking_system) lines.push(`Booked on: ${d.booking_system}`);
      if (d.passport_name) lines.push(`Passenger: ${d.passport_name}`);
      if (d.passport_no) lines.push(`Passport: ${d.passport_no}`);
      if (d.booking_expires) lines.push(`TTL expires: ${fmtWhen(d.booking_expires)}`);
      if (d.pay_before) lines.push(`Pay balance by: ${fmtWhen(d.pay_before)}`);
      if (d.quoted_total != null) lines.push(`Total quoted: ${fmtAmt(d.quoted_total)}`);
      if (d.advance != null) {
        lines.push(`Advance paid: ${fmtAmt(d.advance)}${when ? ` · recorded ${when}` : ''}`);
      }
      if (d.balance_due != null) lines.push(`Balance due: ${fmtAmt(d.balance_due)}`);
      break;

    case 'ticket:booking_confirmed':
      if (d.quoted_total != null) lines.push(`Total quoted: ${fmtAmt(d.quoted_total)}`);
      if (d.advance_paid != null) lines.push(`Advance (from temp booking): ${fmtAmt(d.advance_paid)}`);
      if (d.payment_amount != null) {
        lines.push(`Payment received: ${fmtAmt(d.payment_amount)}${when ? ` · recorded ${when}` : ''}`);
      }
      if (d.paid_on) lines.push(`Payment date: ${fmtDate(d.paid_on)}`);
      if (d.fully_paid) lines.push('Fully paid');
      else if (d.balance_due != null) {
        lines.push(`Balance still due: ${fmtAmt(d.balance_due)}`);
        if (d.remain_pay_by) lines.push(`Remaining due by: ${fmtWhen(d.remain_pay_by)}`);
      }
      if (d.note) lines.push(`Notes: ${d.note}`);
      break;

    case 'ticket:ticket_issued':
      if (d.issued_date) lines.push(`Issued: ${fmtDate(d.issued_date)}`);
      if (d.pnr) lines.push(`PNR: ${d.pnr}`);
      if (d.seat_fee != null) lines.push(`Seat booking fee: ${fmtAmt(d.seat_fee)}`);
      if (d.seats_booked) lines.push('Seats booked as requested');
      if (d.note) lines.push(`Notes: ${d.note}`);
      break;

    case 'ticket:booking_cancelled':
      if (d.penalty != null) lines.push(`Penalty: ${fmtAmt(d.penalty)}`);
      if (d.refund != null) lines.push(`Refunded: ${fmtAmt(d.refund)}`);
      if (d.note) lines.push(`Notes: ${d.note}`);
      break;

    case 'tour:quotation':
      lines.push(fmtPaxLine(d.pax));
      lines.push(fmtRateLine('Agent net cost / person', d.agent_quote));
      lines.push(fmtRateLine('Flight fare / person', d.flight_cost));
      lines.push(fmtRateLine('Visa fee / person', d.visa_cost));
      if (d.total_cost_per) lines.push(fmtRateLine('Total cost / person', d.total_cost_per));
      if (d.cost_total != null) lines.push(`Total cost: ${fmtAmt(d.cost_total)}`);
      lines.push(fmtRateLine('Final quote / person', d.final_quote));
      if (d.sell_total != null) lines.push(`Client quote total: ${fmtAmt(d.sell_total)}`);
      if (d.profit_total != null) lines.push(`Profit: ${fmtAmt(d.profit_total)}`);
      if (d.hotel_rooms_summary || d.hotel_notes) lines.push(`Hotel: ${d.hotel_rooms_summary || d.hotel_notes}`);
      if (d.supplier_note) lines.push(`Supplier notes: ${d.supplier_note}`);
      break;

    case 'tour:quote_request_client':
      if (d.date_from && d.date_to) {
        lines.push(`Dates: ${formatDateRangeLabel(d.date_from, d.date_to)}`);
      } else if (d.custom_dates) lines.push(`Dates: ${d.custom_dates}`);
      if (d.duration) lines.push(`Duration: ${d.duration}`);
      if (d.recommended_dates) lines.push(`Recommended dates: ${d.recommended_dates}`);
      lines.push(fmtPaxLine(d.pax));
      if (d.child_ages) lines.push(`Child ages: ${d.child_ages}`);
      if (d.flights?.airline) lines.push(`Airline: ${d.flights.airline}`);
      if (d.flights?.cabin_class) lines.push(`Cabin: ${d.flights.cabin_class.replace('_', ' ')}`);
      lines.push(fmtFlightSeg('Outbound', d.flights?.outbound));
      if (d.flights?.return) lines.push(fmtFlightSeg('Return', d.flights.return));
      if (d.flights?.include_domestic) {
        lines.push(fmtFlightSeg('Domestic out', d.flights.domestic_outbound));
        lines.push(fmtFlightSeg('Domestic return', d.flights.domestic_return));
      } else if (d.domestic) lines.push(`Domestic: ${d.domestic}`);
      if (d.flights?.baggage?.departure) {
        const bag = d.flights.baggage.same
          ? `Baggage: ${d.flights.baggage.departure} (both ways)`
          : `Baggage: dep ${d.flights.baggage.departure}${d.flights.baggage.arrival ? ` · return ${d.flights.baggage.arrival}` : ''}`;
        lines.push(bag);
      }
      if (d.flights?.notes || d.flight_requests || d.flight_notes) {
        lines.push(`Flight notes: ${d.flights?.notes || d.flight_requests || d.flight_notes}`);
      }
      else if (d.departure || d.arrival) {
        if (d.departure) lines.push(`Departure: ${d.departure}`);
        if (d.arrival) lines.push(`Arrival: ${d.arrival}`);
      }
      if (d.hotel?.rooms?.length) {
        lines.push(`Hotel: ${fmtHotelRooms(d.hotel.rooms)}`);
        if (d.hotel.star_rating) lines.push(`Hotel star: ${d.hotel.star_rating}★`);
        if (d.hotel.preferred_area) lines.push(`Area: ${d.hotel.preferred_area}`);
      } else if (d.hotel?.room_type || d.hotel?.preferred_area) {
        lines.push(`Hotel: ${[d.hotel.room_type, d.hotel.preferred_area].filter(Boolean).join(' · ')}`);
      }
      if (d.hotel?.notes || d.hotel_notes) lines.push(`Hotel notes: ${d.hotel?.notes || d.hotel_notes}`);
      if (d.budget != null) lines.push(`Budget: ${fmtAmt(d.budget)}`);
      if (d.visa_assistance) lines.push('Visa assistance requested');
      if (d.travel_insurance) lines.push('Travel insurance requested');
      if (d.dates_flexible) lines.push('Dates flexible');
      if (d.special_requirements) lines.push(`Special requirements: ${d.special_requirements}`);
      if (d.places_to_visit) lines.push(`Sightseeing: ${d.places_to_visit}`);
      break;

    case 'tour:quote_request_agent':
      if (d.note) lines.push(d.note);
      break;

    case 'tour:quote_received_agent':
      lines.push(fmtPaxLine(d.pax));
      lines.push(fmtRateLine('Agent quote / person', d.agent_quote));
      if (d.total != null) lines.push(`Total cost: ${fmtAmt(d.total)}`);
      break;

    case 'tour:final_quote_sent':
      lines.push(fmtPaxLine(d.pax));
      lines.push(fmtRateLine('Agent quote / person', d.agent_quote));
      lines.push(fmtRateLine('Flight fare / person', d.flight_fare || d.quote_components?.flight_fare));
      lines.push(fmtRateLine('Visa fee / person', d.visa_fee || d.quote_components?.visa_fee));
      lines.push(fmtRateLine('Profit markup / person', d.profit_markup || d.quote_components?.profit_markup));
      lines.push(fmtRateLine('Additional fees / person', d.additional_fees || d.quote_components?.additional_fees));
      if (d.additional_fees_desc) lines.push(`Additional fees: ${d.additional_fees_desc}`);
      lines.push(fmtRateLine('Final quote / person', d.final_quote));
      if (d.total != null) lines.push(`Total quotation: ${fmtAmt(d.total)}`);
      if (d.note) lines.push(`Notes: ${d.note}`);
      break;

    case 'tour:quotation':
      lines.push(fmtPaxLine(d.pax));
      lines.push(fmtRateLine('Agent net cost / person', d.agent_quote));
      lines.push(fmtRateLine('Flight fare / person', d.flight_cost));
      lines.push(fmtRateLine('Visa fee / person', d.visa_cost));
      if (d.total_cost_per) lines.push(fmtRateLine('Total cost / person', d.total_cost_per));
      if (d.cost_total != null) lines.push(`Total cost: ${fmtAmt(d.cost_total)}`);
      lines.push(fmtRateLine('Final quote / person', d.final_quote));
      if (d.sell_total != null) lines.push(`Client quote total: ${fmtAmt(d.sell_total)}`);
      if (d.profit_total != null) lines.push(`Profit: ${fmtAmt(d.profit_total)}`);
      if (d.hotel_rooms_summary || d.hotel_notes) lines.push(`Hotel: ${d.hotel_rooms_summary || d.hotel_notes}`);
      if (d.supplier_note) lines.push(`Supplier notes: ${d.supplier_note}`);
      break;

    case 'tour:tour_accepted_advance':
      if (d.quoted_total != null) lines.push(`Total quotation: ${fmtAmt(d.quoted_total)}`);
      if (d.travel_date) lines.push(`Travel date: ${fmtDate(d.travel_date)}`);
      if (d.advance_date) lines.push(`Advance paid: ${fmtDate(d.advance_date)}`);
      if (d.advance_amount != null) lines.push(`Advance amount: ${fmtAmt(d.advance_amount)}`);
      if (d.balance_due != null) lines.push(`Remaining to collect: ${fmtAmt(d.balance_due)}`);
      if (d.balance_collect_date) lines.push(`Collect balance by: ${fmtDate(d.balance_collect_date)}`);
      break;

    case 'tour:tour_accepted_progress':
      if (d.progress_notes || d.ticket_details) lines.push(`Progress: ${d.progress_notes || d.ticket_details}`);
      if (d.ticket_booked_on) lines.push(`Tickets booked: ${fmtDate(d.ticket_booked_on)}`);
      if (d.visa_issued_date) lines.push(`Visa issued: ${fmtDate(d.visa_issued_date)}`);
      else if (d.visa_applied_date) lines.push(`Visa applied: ${fmtDate(d.visa_applied_date)}`);
      if (d.followup_date) lines.push(`Follow-up: ${fmtDate(d.followup_date)}`);
      break;

    case 'tour:tour_finalised':
      if (d.ticket_booked_on) lines.push(`TKT booked: ${fmtDate(d.ticket_booked_on)}`);
      if (d.visa_issued_date) lines.push(`Visa issued: ${fmtDate(d.visa_issued_date)}`);
      if (d.note) lines.push(d.note);
      break;

    case 'visa:client_not_interested':
    case 'tour:client_not_interested':
    case 'ticket:client_not_interested':
    case 'ticket:new':
    case 'tour:new':
    case 'visa:new':
      if (type === 'ticket' && isTicketQuickIntake(lead)) {
        const intake = ticketIntakeText(lead);
        if (intake) lines.push(intake);
      } else if (d.note) {
        lines.push(d.note);
      }
      break;

    default:
      if (d.note) lines.push(d.note);
      else {
        const hint = getStageHint(type, status);
        if (hint) lines.push(hint);
      }
      break;
  }

  return lines.filter(Boolean).join('\n');
}

const STAGE_TAB_SHORT = {
  new: 'New',
  details_provided: 'Details',
  visa_applied: 'Applied',
  visa_issued: 'Issued',
  quotation_sent: 'Quote',
  temporary_booking: 'Temp book',
  booking_confirmed: 'Payment',
  ticket_issued: 'TKT issued',
  quote_request_client: 'Client req',
  quote_request_agent: 'Agent req',
  quote_received_agent: 'Agent quote',
  final_quote_sent: 'Final quote',
  quotation: 'Quotation',
  tour_accepted_advance: 'Advance',
  tour_accepted_progress: 'Progress',
  tour_finalised: 'Finalised',
};

export function stageTabShortLabel(status) {
  if (STAGE_TAB_SHORT[status]) return STAGE_TAB_SHORT[status];
  const full = STATUS_LABELS[status] || status;
  return full.length > 14 ? `${full.slice(0, 12)}…` : full;
}

export function stageHasSavedData(lead, status) {
  const sd = lead?.extra?.stage_data?.[status];
  if (sd && Object.keys(sd).length) return true;
  return false;
}

/** Read-only saved stage details for the status tab viewer. */
export function renderStageSavedViewHtml(type, status, lead = {}) {
  const extra = lead.extra || {};
  const sd = extra.stage_data?.[status];
  const prefix = (type === 'ticket' && isTicketQuickIntake(lead) && status !== 'new')
    ? ticketIntakeSummaryHtml(lead, { compact: true })
    : '';
  const text = formatStageHistoryDescription(type, status, sd || {}, lead);
  const lines = text.split('\n').filter(Boolean);
  if (!lines.length && !prefix) {
    return '<p class="stage-saved-empty">No saved details recorded for this stage.</p>';
  }
  const rows = lines.map((line) => {
    const colon = line.indexOf(':');
    if (colon > 0 && colon < 48) {
      const label = line.slice(0, colon).trim();
      const value = line.slice(colon + 1).trim();
      return `<div class="stage-saved-field"><span class="stage-saved-label">${esc(label)}</span><span class="stage-saved-value">${esc(value)}</span></div>`;
    }
    return `<div class="stage-saved-field stage-saved-field-full"><span class="stage-saved-value">${esc(line)}</span></div>`;
  }).join('');
  const body = lines.length
    ? `<div class="stage-saved-summary surface-1"><div class="stage-saved-grid">${rows}</div></div>`
    : '';
  return `${prefix}${body}`;
}

export function statusHistoryTitle(type, status, fromStatus) {
  const label = STATUS_LABELS[status] || status;
  if (fromStatus && fromStatus !== status) {
    const fromLabel = STATUS_LABELS[fromStatus] || fromStatus;
    return `${fromLabel} → ${label}`;
  }
  return label;
}

export function validateStageFormClient(type, status, lead = {}) {
  const body = collectStageFormData(type, status, lead);
  if (status === 'client_not_interested' && !body.note) {
    return 'Enter the reason';
  }
  if (status === 'booking_cancelled' && !body.note) {
    return 'Enter cancellation notes';
  }
  if (type === 'visa' && status === 'details_provided' && !body.amount) return 'Enter quoted price';
  if (type === 'visa' && status === 'visa_applied') {
    if (!body.stage_data.application_id) return 'Enter application ID';
    if (!body.stage_data.passport_no) return 'Enter passport number';
  }
  if (type === 'ticket' && status === 'quotation_sent') {
    if (!body.stage_data.quoted_at) return 'Enter quotation sent date/time';
    if (isTicketQuickIntake(lead)) return null;
    if (!body.airline) return 'Enter airline';
    if (!body.quote_amount && !body.amount) return 'Enter per-pax cost and profit rates';
  }
  if (type === 'ticket' && status === 'temporary_booking') {
    if (!body.stage_data.pnr && !body.pnr) return 'Enter PNR';
    if (!body.stage_data.booking_system) return 'Select where the booking was made';
    if (txt('stBookingSystem') === 'Other' && !txt('stBookingSystemOther')) {
      return 'Enter where the booking was made';
    }
    if (!body.stage_data.booking_expires) return 'Enter booking expiry';
    if (isTicketQuickIntake(lead)) {
      const pax = body.extra_patch?.pax || quotationPaxFromLead(lead);
      if (!pax.adt && !pax.chd && !pax.inf) return 'Enter passenger counts';
      if (!body.quote_amount && !body.amount) return 'Enter per-pax cost and profit rates';
    }
  }
  if (type === 'ticket' && status === 'booking_confirmed') {
    if (!body.stage_data.paid_on) return 'Enter paid date';
    if (!body.sold_amount) return 'Enter payment amount';
    const quoted = body.stage_data.quoted_total || 0;
    if (!body.stage_data.fully_paid && quoted > 0 && !body.stage_data.remain_pay_by) {
      return 'Enter when the remaining balance will be paid';
    }
  }
  if (type === 'ticket' && status === 'ticket_issued') {
    if (!body.stage_data.issued_date) return 'Enter issued date';
    if (!body.stage_data.pnr) return 'Enter PNR';
  }
  if (type === 'tour' && status === 'quote_request_client') {
    if (!body.stage_data.date_from && !body.stage_data.date_to && !body.stage_data.duration && !body.stage_data.custom_dates) {
      return 'Select requested travel dates';
    }
    if (body.stage_data.date_from && body.stage_data.date_to && body.stage_data.date_to < body.stage_data.date_from) {
      return 'Return date must be on or after departure date';
    }
  }
  if (type === 'tour' && status === 'quotation') {
    if (!body.cost_amount) return 'Enter agent net cost per adult (LKR).';
    if (!body.amount) return 'Enter final quote per adult (LKR).';
  }
  if (type === 'tour' && status === 'tour_accepted_advance') {
    if (!body.stage_data.travel_date) return 'Enter travel date';
    if (!body.stage_data.advance_date) return 'Enter advance payment date';
    if (body.advance_paid == null) return 'Enter advance amount';
    const balanceDue = Number(body.stage_data.balance_due ?? 0);
    if (balanceDue > 0 && !body.stage_data.balance_collect_date) return 'Enter balance collect date';
    if (!body.stage_data.quoted_total) return 'Total quotation missing — complete Quotation first';
    if (body.advance_paid > body.stage_data.quoted_total) return 'Advance cannot exceed total quotation';
  }
  if (type === 'tour' && status === 'tour_finalised') {
    return null;
  }
  return null;
}

const STAGE_FORM_STATUSES = new Set([
  'client_not_interested',
  'booking_cancelled',
  'new',
  'details_provided',
  'visa_applied',
  'visa_issued',
  'quotation_sent',
  'temporary_booking',
  'booking_confirmed',
  'ticket_issued',
  'quotation',
  'tour_accepted_advance',
  'tour_accepted_progress',
  'tour_finalised',
]);

export function statusHasStageForm(_type, status) {
  return STAGE_FORM_STATUSES.has(status);
}

/** True when Proceed should stay disabled (saved data incomplete and form invalid). */
export function stageProceedBlocked(type, status, lead = {}) {
  if (!statusHasStageForm(type, status)) return false;
  const formHost = document.getElementById('dStageFields');
  const modalHost = document.getElementById('stStageFields');
  const hasInputs = !!(formHost?.querySelector('[id^="st"]') || modalHost?.querySelector('[id^="st"]'));
  if (hasInputs) return !!validateStageFormClient(type, status, lead);
  return leadStageIncomplete(type, status, lead);
}

function pickVal(...vals) {
  return vals.find((v) => v != null && v !== '');
}

export function leadStageIncomplete(type, status, lead = {}) {
  if (!statusHasStageForm(type, status)) return false;
  const extra = lead.extra || {};
  const sd = extra.stage_data?.[status] || {};

  switch (`${type}:${status}`) {
    case 'visa:details_provided':
      return sd.quote_price == null;
    case 'visa:visa_applied':
      return sd.application_id == null || sd.application_id === ''
        || sd.passport_no == null || sd.passport_no === '';
    case 'ticket:quotation_sent':
      if (isTicketQuickIntake(lead)) {
        return !pickVal(sd.quoted_at, extra.quoted_at);
      }
      return !pickVal(sd.quoted_at, extra.quoted_at)
        || !pickVal(sd.airline, lead.airline)
        || (!pickVal(lead.quote_amount, lead.amount, extra.quoted_total, sd.total)
          && !pickVal(sd.cost_adt, extra.pax_cost_rates?.adt));
    case 'ticket:temporary_booking': {
      const base = !pickVal(sd.pnr, lead.pnr)
        || !pickVal(sd.booking_system, extra.booking_system)
        || !pickVal(sd.booking_expires, extra.booking_expires);
      if (!isTicketQuickIntake(lead)) return base;
      return base
        || (!pickVal(lead.quote_amount, lead.amount, extra.quoted_total, sd.total)
          && !pickVal(sd.cost_adt, extra.pax_cost_rates?.adt));
    }
    case 'ticket:booking_confirmed':
      return !pickVal(sd.paid_on, extra.paid_on) || (sd.payment_amount == null && lead.sold_amount == null);
    case 'ticket:ticket_issued':
      return !pickVal(sd.issued_date, extra.ticket_issued_date, sd.issued_date)
        || !pickVal(sd.pnr, lead.pnr);
    case 'tour:quotation':
      return !pickVal(lead.cost_amount, sd.cost_total, sd.agent_quote?.adt)
        || !pickVal(lead.amount, sd.sell_total, sd.final_quote?.adt);
    case 'tour:tour_accepted_advance': {
      const bal = Number(extra.balance_due ?? sd.balance_due ?? 0);
      return !pickVal(sd.travel_date, extra.travel_date, lead.depart_date)
        || (sd.advance_amount == null && extra.advance_paid == null)
        || !pickVal(sd.advance_date, extra.advance_date)
        || (bal > 0 && !pickVal(sd.balance_collect_date, extra.balance_collect_date));
    }
    case 'tour:tour_accepted_progress':
      return false;
    case 'tour:tour_finalised':
      return false;
    default:
      return false;
  }
}
